import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EyeIcon, DownloadIcon, FileTextIcon, FileDownIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon, ImageIcon, XIcon, ChevronUpIcon, ChevronDownIcon, EditIcon, ClockIcon, RefreshCwIcon, CheckIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { ClinicalHistory as ClinicalHistoryType } from '../../types';
import { formatDate, getDoctorPrefix } from '../../utils/helpers';
import { exportClinicalHistoriesToExcel } from '../../utils/excelExport';
import { exportClinicalHistoriesToPdf } from '../../utils/pdfExport';
import { generateClinicalHistoryPdf } from '../../utils/clinicalHistoryPdf';
import { FormSection } from '../ui/FormSection';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { useConfirm } from '../../contexts/ConfirmContext';
export function ClinicalHistory() {
  const {
    patients,
    getPatient,
    doctors,
    clinicalHistories,
    addClinicalHistory,
    updateClinicalHistory,
    appointments,
    services,
    getSpecialty
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  // ✅ Usar hook centralizado de permisos
  const {
    isAdmin,
    canExport
  } = useRolePermissions();
  const {
    confirm
  } = useConfirm();
  const readOnly = isReadOnly('clinicalHistory');
  const canExportExcel = canExport('clinicalHistory');
  const canExportPdf = canExport('clinicalHistory');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingHistory, setEditingHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  // Retomar historial state
  const [previousHistoryForPatient, setPreviousHistoryForPatient] = useState<ClinicalHistoryType | null>(null);
  const [retakeApplied, setRetakeApplied] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-clinical-history-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | null>(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateClinicalHistory(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateClinicalHistory(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateClinicalHistory(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const openActionConfirm = (history: ClinicalHistoryType, type: 'archive' | 'deactivate') => {
    setSelectedHistory(history);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Historial';
      message = '¿Estás seguro de que deseas archivar este historial clínico? Podrás restaurarlo más tarde.';
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Historial';
      message = '¿Estás seguro de que deseas desactivar este historial clínico? No aparecerá en las listas activas.';
      confirmText = 'Desactivar';
      variant = 'warning';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          archiveItem(history.id);
          showToast({
            type: 'info',
            title: 'Historial archivado',
            message: 'El historial clínico fue archivado.'
          });
        } else if (type === 'deactivate') {
          deactivateItem(history.id);
          showToast({
            type: 'warning',
            title: 'Historial desactivado',
            message: 'El historial clínico fue desactivado.'
          });
        }
      }
    });
  };
  // Find the doctor record that matches the current user (by email)
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctor = !!currentUserDoctor && !isAdmin;
  // Load deactivated/archived statuses for related entities
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const ps = localStorage.getItem('savidna-patients-status');
      if (ps) setPatientStatuses(JSON.parse(ps));
      const ds = localStorage.getItem('savidna-doctors-status');
      if (ds) setDoctorStatuses(JSON.parse(ds));
    } catch {}
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-patients-status' && e.newValue) {
        try {
          setPatientStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isPatientActive = (id: string) => !patientStatuses[id];
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  // Form state - Antecedentes Personales
  const [personalData, setPersonalData] = useState({
    fullName: '',
    birthDate: '',
    address: '',
    phone: '',
    email: '',
    age: 0,
    gender: '',
    occupation: '',
    numberOfChildren: 0,
    deliveryType: '',
    alcohol: '',
    tobacco: '',
    drugs: '',
    infusions: '',
    consultationReason: ''
  });
  // Form state - Antecedentes Clínicos
  const [clinicalData, setClinicalData] = useState({
    diseases: '',
    currentMedications: '',
    chronicDiseases: '',
    surgicalHistory: '',
    implantsOrDevices: '',
    implantType: '',
    allergies: ''
  });
  // Form state - Exploración Física
  const [physicalExamData, setPhysicalExamData] = useState({
    bloodPressure: '',
    bloodSugar: '',
    heartRate: '',
    lungs: '',
    eyes: '',
    throat: '',
    abdomen: '',
    weight: '',
    height: '',
    temperature: '',
    positiveAspects: '',
    respiratoryRate: '',
    weightLb: ''
  });
  // Form state - Historia de la Enfermedad Actual
  const [diseaseHistoryData, setDiseaseHistoryData] = useState({
    sinceWhen: '',
    previousTreatment: '',
    evolution: ''
  });
  // Form state - Revisión por Sistemas
  const [systemsReviewData, setSystemsReviewData] = useState({
    general: '',
    cardioRespiratory: '',
    gastrointestinal: '',
    genitourinary: '',
    osteoMuscular: '',
    neurological: '',
    skinAndAppendages: ''
  });
  // Form state - Diagnóstico, Tratamiento, etc.
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [medicalIndications, setMedicalIndications] = useState('');
  const [selectedSessionServiceIds, setSelectedSessionServiceIds] = useState<string[]>([]);
  const [sessionNumber, setSessionNumber] = useState('');
  const [sessionServiceNumbers, setSessionServiceNumbers] = useState<Record<string, string>>({});
  const [comments, setComments] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [sessionServiceSearch, setSessionServiceSearch] = useState('');
  const [attachedImages, setAttachedImages] = useState<{
    name: string;
    data: string;
  }[]>([]);
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Character limits for print-safe fields (priority order)
  const CHAR_LIMITS = {
    diagnosis: 500,
    treatment: 1000,
    medicalIndications: 500,
    comments: 300
  };
  // Today's date for max restriction
  const today = new Date().toISOString().split('T')[0];
  const filteredHistories = useMemo(() => clinicalHistories.filter(h => {
    const patient = getPatient(h.patientId);
    const matchesSearch = patient?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(h.id);
    const matchesStatus = status === viewMode;
    if (isDoctor && currentUserDoctor) {
      return matchesSearch && matchesStatus && h.doctorId === currentUserDoctor.id;
    }
    return matchesSearch && matchesStatus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [clinicalHistories, searchTerm, viewMode, isDoctor, currentUserDoctor, itemStatuses]);
  const {
    sortedItems: sortedHistories,
    requestSort: chRequestSort,
    isSorted: chIsSorted
  } = useSortableData(filteredHistories, {
    key: 'date',
    direction: 'desc'
  });
  // Pagination for clinical histories
  const {
    paginatedItems: paginatedHistories,
    currentPage: chCurrentPage,
    totalPages: chTotalPages,
    totalItems: chTotalItems,
    startIndex: chStartIndex,
    endIndex: chEndIndex,
    goToPage: chGoToPage,
    nextPage: chNextPage,
    prevPage: chPrevPage
  } = usePagination(sortedHistories, {
    itemsPerPage: 10
  });
  // Counts for tabs
  const activeCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'active').length;
  const archivedCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'archived').length;
  const deactivatedCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'deactivated').length;
  // Show ALL active patients (no doctor-based filtering)
  const availablePatients = patients.filter(p => isPatientActive(p.id));
  const patientOptions = availablePatients.map(p => ({
    value: p.id,
    label: p.name
  }));
  // Filter doctors based on selected patient's appointments
  const doctorOptions = doctors.filter(d => {
    if (!isDoctorActive(d.id)) return false;
    if (!selectedPatientId) return true;
    const hasAppointment = appointments.some(a => a.patientId === selectedPatientId && a.doctorId === d.id && a.status === 'completada');
    return hasAppointment;
  }).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  }));
  const openNewForm = () => {
    setSelectedHistory(null);
    setSelectedPatientId('');
    setSaveError('');
    setPreviousHistoryForPatient(null);
    setRetakeApplied(false);
    resetForm();
    setIsFormOpen(true);
  };
  const openViewHistory = (history: ClinicalHistoryType) => {
    setSelectedHistory(history);
    setIsViewOpen(true);
  };
  const openEditHistory = (history: ClinicalHistoryType) => {
    setEditingHistory(history);
    setSelectedPatientId(history.patientId);
    setSaveError('');
    // Populate personal background
    setPersonalData({
      fullName: history.personalBackground.fullName || '',
      birthDate: history.personalBackground.birthDate || '',
      address: history.personalBackground.address || '',
      phone: history.personalBackground.phone || '',
      email: history.personalBackground.email || '',
      age: history.personalBackground.age || 0,
      gender: history.personalBackground.gender || '',
      occupation: history.personalBackground.occupation || '',
      numberOfChildren: history.personalBackground.numberOfChildren || 0,
      deliveryType: history.personalBackground.deliveryType || '',
      alcohol: history.personalBackground.alcohol || '',
      tobacco: history.personalBackground.tobacco || '',
      drugs: history.personalBackground.drugs || '',
      infusions: history.personalBackground.infusions || '',
      consultationReason: history.personalBackground.consultationReason || ''
    });
    // Populate clinical background
    setClinicalData({
      diseases: history.clinicalBackground.diseases || '',
      currentMedications: history.clinicalBackground.currentMedications || '',
      chronicDiseases: history.clinicalBackground.chronicDiseases || '',
      surgicalHistory: history.clinicalBackground.surgicalHistory || '',
      implantsOrDevices: history.clinicalBackground.implantsOrDevices || '',
      implantType: history.clinicalBackground.implantType || '',
      allergies: history.clinicalBackground.allergies || ''
    });
    // Populate physical exam
    setPhysicalExamData({
      bloodPressure: history.physicalExam?.bloodPressure || '',
      bloodSugar: history.physicalExam?.bloodSugar || '',
      heartRate: history.physicalExam?.heartRate || '',
      lungs: history.physicalExam?.lungs || '',
      eyes: history.physicalExam?.eyes || '',
      throat: history.physicalExam?.throat || '',
      abdomen: history.physicalExam?.abdomen || '',
      weight: history.physicalExam?.weight || '',
      height: history.physicalExam?.height || '',
      temperature: history.physicalExam?.temperature || '',
      positiveAspects: (history.physicalExam as any)?.positiveAspects || '',
      respiratoryRate: (history.physicalExam as any)?.respiratoryRate || '',
      weightLb: (history.physicalExam as any)?.weightLb || ''
    });
    setDiseaseHistoryData({
      sinceWhen: (history as any).diseaseHistory?.sinceWhen || '',
      previousTreatment: (history as any).diseaseHistory?.previousTreatment || '',
      evolution: (history as any).diseaseHistory?.evolution || ''
    });
    setSystemsReviewData({
      general: (history as any).systemsReview?.general || '',
      cardioRespiratory: (history as any).systemsReview?.cardioRespiratory || '',
      gastrointestinal: (history as any).systemsReview?.gastrointestinal || '',
      genitourinary: (history as any).systemsReview?.genitourinary || '',
      osteoMuscular: (history as any).systemsReview?.osteoMuscular || '',
      neurological: (history as any).systemsReview?.neurological || '',
      skinAndAppendages: (history as any).systemsReview?.skinAndAppendages || ''
    });
    setDiagnosis(history.diagnosis || '');
    setTreatment(history.treatment || '');
    setMedicalIndications(history.medicalIndications || '');
    setComments(history.comments || '');
    // Populate session services
    const svcIds = (history.sessionServices || []).map(ss => ss.serviceId);
    setSelectedSessionServiceIds(svcIds);
    const nums: Record<string, string> = {};
    (history.sessionServices || []).forEach(ss => {
      nums[ss.serviceId] = ss.sessionNumber || '1';
    });
    setSessionServiceNumbers(nums);
    setAttachedImages(history.attachedImages || []);
    setFormErrors({});
    setSessionServiceSearch('');
    setIsEditFormOpen(true);
  };
  const resetForm = () => {
    setPersonalData({
      fullName: '',
      birthDate: '',
      address: '',
      phone: '',
      email: '',
      age: 0,
      gender: '',
      occupation: '',
      numberOfChildren: 0,
      deliveryType: '',
      alcohol: '',
      tobacco: '',
      drugs: '',
      infusions: '',
      consultationReason: ''
    });
    setClinicalData({
      diseases: '',
      currentMedications: '',
      chronicDiseases: '',
      surgicalHistory: '',
      implantsOrDevices: '',
      implantType: '',
      allergies: ''
    });
    setPhysicalExamData({
      bloodPressure: '',
      bloodSugar: '',
      heartRate: '',
      lungs: '',
      eyes: '',
      throat: '',
      abdomen: '',
      weight: '',
      height: '',
      temperature: '',
      positiveAspects: '',
      respiratoryRate: '',
      weightLb: ''
    });
    setDiseaseHistoryData({
      sinceWhen: '',
      previousTreatment: '',
      evolution: ''
    });
    setSystemsReviewData({
      general: '',
      cardioRespiratory: '',
      gastrointestinal: '',
      genitourinary: '',
      osteoMuscular: '',
      neurological: '',
      skinAndAppendages: ''
    });
    setDiagnosis('');
    setTreatment('');
    setMedicalIndications('');
    setSelectedSessionServiceIds([]);
    setSessionNumber('');
    setSessionServiceNumbers({});
    setComments('');
    setFormErrors({});
    setSessionServiceSearch('');
    setAttachedImages([]);
    setViewingImage(null);
  };
  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    setRetakeApplied(false);
    const patient = getPatient(patientId);
    if (patient) {
      // If patient is male, set delivery type to N/A automatically
      const isMale = patient.gender?.toLowerCase() === 'masculino';
      setPersonalData({
        fullName: patient.name,
        birthDate: '',
        address: patient.address,
        phone: patient.phone,
        email: patient.email,
        age: 0,
        gender: patient.gender,
        occupation: '',
        numberOfChildren: 0,
        deliveryType: isMale ? 'N/A' : '',
        alcohol: '',
        tobacco: '',
        drugs: '',
        infusions: '',
        consultationReason: ''
      });
    }
    // Find most recent clinical history for this patient
    const patientHistories = clinicalHistories.filter(h => h.patientId === patientId).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setPreviousHistoryForPatient(patientHistories[0] || null);
  };
  // Pre-fill form with data from previous history (antecedentes only, not visit-specific fields)
  const handleRetakeHistory = () => {
    if (!previousHistoryForPatient) return;
    const h = previousHistoryForPatient;
    setPersonalData({
      fullName: h.personalBackground.fullName || '',
      birthDate: h.personalBackground.birthDate || '',
      address: h.personalBackground.address || '',
      phone: h.personalBackground.phone || '',
      email: h.personalBackground.email || '',
      age: h.personalBackground.age || 0,
      gender: h.personalBackground.gender || '',
      occupation: h.personalBackground.occupation || '',
      numberOfChildren: h.personalBackground.numberOfChildren || 0,
      deliveryType: h.personalBackground.deliveryType || '',
      alcohol: h.personalBackground.alcohol || '',
      tobacco: h.personalBackground.tobacco || '',
      drugs: h.personalBackground.drugs || '',
      infusions: h.personalBackground.infusions || '',
      consultationReason: '' // leave blank — new visit reason
    });
    setClinicalData({
      diseases: h.clinicalBackground.diseases || '',
      currentMedications: h.clinicalBackground.currentMedications || '',
      chronicDiseases: h.clinicalBackground.chronicDiseases || '',
      surgicalHistory: h.clinicalBackground.surgicalHistory || '',
      implantsOrDevices: h.clinicalBackground.implantsOrDevices || '',
      implantType: h.clinicalBackground.implantType || '',
      allergies: h.clinicalBackground.allergies || ''
    });
    setPhysicalExamData({
      bloodPressure: '',
      bloodSugar: '',
      heartRate: '',
      lungs: '',
      eyes: '',
      throat: '',
      abdomen: '',
      weight: (h.physicalExam as any)?.weight || '',
      height: (h.physicalExam as any)?.height || '',
      temperature: '',
      positiveAspects: '',
      respiratoryRate: '',
      weightLb: (h.physicalExam as any)?.weightLb || ''
    });
    setDiseaseHistoryData({
      sinceWhen: (h as any).diseaseHistory?.sinceWhen || '',
      previousTreatment: (h as any).diseaseHistory?.previousTreatment || '',
      evolution: '' // leave blank — new evolution
    });
    setSystemsReviewData({
      general: (h as any).systemsReview?.general || '',
      cardioRespiratory: (h as any).systemsReview?.cardioRespiratory || '',
      gastrointestinal: (h as any).systemsReview?.gastrointestinal || '',
      genitourinary: (h as any).systemsReview?.genitourinary || '',
      osteoMuscular: (h as any).systemsReview?.osteoMuscular || '',
      neurological: (h as any).systemsReview?.neurological || '',
      skinAndAppendages: (h as any).systemsReview?.skinAndAppendages || ''
    });
    setRetakeApplied(true);
    // Scroll to top so user sees the form is populated
    setTimeout(() => {
      const modal = document.querySelector('.modal-content-scrollable');
      if (modal) modal.scrollTop = 0;
    }, 100);
  };
  const scrollToTop = () => {
    // Try to scroll the modal content
    const modal = document.querySelector('.modal-content-scrollable');
    if (modal) {
      modal.scrollTop = 0;
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const scrollToFirstError = (errors: Record<string, string>) => {
    // Map error keys to DOM element selectors
    const errorFieldMap: Record<string, string> = {
      patient: '[data-field="patient"]',
      fullName: '[data-field="fullName"]',
      doctor: '[data-field="doctor"]'
    };
    for (const key of Object.keys(errors)) {
      const selector = errorFieldMap[key];
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          // Add a brief highlight effect
          element.classList.add('ring-2', 'ring-red-400', 'ring-offset-2', 'rounded-lg');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2', 'rounded-lg');
          }, 3000);
          return;
        }
      }
    }
    // Fallback: scroll to top of modal
    const modal = document.querySelector('.modal-content-scrollable');
    if (modal) {
      modal.scrollTop = 0;
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaveError('Por favor complete los campos requeridos marcados en rojo.');
      setTimeout(() => scrollToFirstError(errors), 100);
      return;
    }
    setIsSaving(true);
    setSaveError('');
    // Auto-assign doctor from current user
    const autoDoctor = currentUserDoctor;
    const autoDoctorId = autoDoctor?.id || user?.id || '';
    const autoDoctorName = autoDoctor?.name || user?.name || '';
    const now = new Date();
    const localDateStr = now.toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    const localDate = new Date(localDateStr + 'T12:00:00');
    // Build sessions string from selected services
    const sessionsText = selectedSessionServiceIds.map(id => services.find(s => s.id === id)?.name).filter(Boolean).join(', ');
    // Build per-service session data
    const sessionServicesData = selectedSessionServiceIds.map(id => {
      const svc = services.find(s => s.id === id);
      return svc ? {
        serviceId: id,
        serviceName: svc.name,
        sessionNumber: sessionServiceNumbers[id] || '1'
      } : null;
    }).filter(Boolean) as {
      serviceId: string;
      serviceName: string;
      sessionNumber: string;
    }[];
    confirm({
      title: 'Guardar Historial',
      message: '¿Estás seguro de que deseas guardar este nuevo historial clínico?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          const savedHistory = await addClinicalHistory({
            patientId: selectedPatientId,
            date: localDate,
            doctorId: autoDoctorId,
            doctorName: autoDoctorName,
            personalBackground: personalData,
            clinicalBackground: clinicalData,
            physicalExam: physicalExamData,
            diseaseHistory: diseaseHistoryData,
            systemsReview: systemsReviewData,
            diagnosis,
            treatment,
            medicalIndications,
            sessions: sessionsText,
            sessionNumber,
            sessionServices: sessionServicesData,
            comments,
            doctorSignature: autoDoctor ? `${getDoctorPrefix(autoDoctor.gender, getSpecialty(autoDoctor.specialtyId)?.name)} ${autoDoctor.name}` : user?.name || '',
            attachedImages: attachedImages
          } as any);
          // If images were attached, auto-generate PDF with images only (documents can't be embedded)
          const imageAttachments = attachedImages.filter(isImageAttachment);
          if (imageAttachments.length > 0) {
            try {
              const patient = getPatient(selectedPatientId);
              await generateClinicalHistoryPdf(savedHistory, patient?.name, imageAttachments);
            } catch (pdfErr) {
              logger.error('Error generando PDF con imágenes:', pdfErr);
            }
          }
          showToast({
            type: 'success',
            title: 'Historial guardado',
            message: 'El historial clínico fue guardado correctamente.'
          });
          setIsFormOpen(false);
          resetForm();
        } catch (error: any) {
          logger.error('❌ Error guardando historial clínico:', error);
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudo guardar el historial clínico.'
          });
          setSaveError(error?.message || 'Error al guardar el historial clínico. Verifica la consola para más detalles.');
          const modal = document.querySelector('.modal-content-scrollable');
          if (modal) {
            modal.scrollTop = 0;
          }
        } finally {
          setIsSaving(false);
        }
      }
    });
  };
  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = clinicalHistories;
    if (dateFrom && dateTo) {
      dataToExport = clinicalHistories.filter(h => {
        const dateStr = new Date(h.date).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportClinicalHistoriesToExcel(dataToExport, (patientId: string) => {
      const patient = getPatient(patientId);
      return patient?.name || '-';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = clinicalHistories;
    if (dateFrom && dateTo) {
      dataToExport = clinicalHistories.filter(h => {
        const dateStr = new Date(h.date).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportClinicalHistoriesToPdf(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-');
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const handleDownloadPdf = async (history: ClinicalHistoryType) => {
    setIsGeneratingPdf(true);
    try {
      const patient = getPatient(history.patientId);
      // Pass only image attachments to the PDF (documents can't be embedded as images)
      await generateClinicalHistoryPdf(history, patient?.name, (history.attachedImages || []).filter(isImageAttachment));
    } catch (error) {
      logger.error('Error generando PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // Handle file attachment (images and documents)
  const handleImageAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`El archivo "${file.name}" es demasiado grande. Máximo 10MB.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedImages(prev => [...prev, {
          name: file.name,
          data: reader.result as string
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  // Detect whether an attachment is an image (otherwise treat as document)
  const isImageAttachment = (att: {
    name: string;
    data: string;
  }) => att.data?.startsWith('data:image') || /\.(png|jpe?g|gif|webp|bmp|svg|heic)$/i.test(att.name || '');
  // Accepted MIME/extensions for the file inputs
  const ATTACH_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  // Available services for session combobox
  const filteredSessionServices = services.filter(s => sessionServiceSearch ? s.name.toLowerCase().includes(sessionServiceSearch.toLowerCase()) : true);
  // Toggle session service selection
  const toggleSessionService = (serviceId: string) => {
    setSelectedSessionServiceIds(prev => {
      if (prev.includes(serviceId)) {
        // Remove service - also clean up its session number
        setSessionServiceNumbers(nums => {
          const next = {
            ...nums
          };
          delete next[serviceId];
          return next;
        });
        return prev.filter(id => id !== serviceId);
      } else {
        // Add service - initialize session number to "1"
        setSessionServiceNumbers(nums => ({
          ...nums,
          [serviceId]: '1'
        }));
        return [...prev, serviceId];
      }
    });
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingHistory) return;
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaveError('Por favor complete los campos requeridos marcados en rojo.');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    const sessionsText = selectedSessionServiceIds.map(id => services.find(s => s.id === id)?.name).filter(Boolean).join(', ');
    const sessionServicesData = selectedSessionServiceIds.map(id => {
      const svc = services.find(s => s.id === id);
      return svc ? {
        serviceId: id,
        serviceName: svc.name,
        sessionNumber: sessionServiceNumbers[id] || '1'
      } : null;
    }).filter(Boolean) as {
      serviceId: string;
      serviceName: string;
      sessionNumber: string;
    }[];
    confirm({
      title: 'Guardar Cambios',
      message: '¿Estás seguro de que deseas guardar los cambios en este historial clínico?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          await updateClinicalHistory(editingHistory.id, {
            patientId: selectedPatientId,
            personalBackground: personalData,
            clinicalBackground: clinicalData,
            physicalExam: physicalExamData,
            diseaseHistory: diseaseHistoryData,
            systemsReview: systemsReviewData,
            diagnosis,
            treatment,
            medicalIndications,
            sessions: sessionsText,
            sessionServices: sessionServicesData,
            comments,
            attachedImages
          } as any);
          setIsEditFormOpen(false);
          setEditingHistory(null);
          resetForm();
        } catch (error: any) {
          logger.error('❌ Error actualizando historial clínico:', error);
          setSaveError(error?.message || 'Error al actualizar el historial clínico.');
        } finally {
          setIsSaving(false);
        }
      }
    });
  };
  return <div className="space-y-6" data-id="element-1476" data-mp="3in352zhpcz3x">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 shadow-xl text-white" data-id="element-1477" data-mp="3nkmhdtuub1t8">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px),repeating-linear-gradient(90deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px)'
      }} data-id="element-1478" data-mp="3smbtzdxpnnjf" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-1479" data-mp="33x4zsj51w6jl" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-1480" data-mp="3hvuy223lwc9j" />
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.15) 100%)'
      }} data-id="element-1481" data-mp="3dvkflpymntds" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-1482" data-mp="2yytpy8er9w1d" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-1483" data-mp="2u6tizw09q2va">
          <div data-id="element-1484" data-mp="26fxxfgmeo3wh">
            <div className="flex items-center gap-3 mb-2" data-id="element-1485" data-mp="26fo2rfqf18kg">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-1486" data-mp="22gp0y3zzdrx8">
                <FileTextIcon className="w-6 h-6 text-white" data-id="element-1487" data-mp="1saxlln039p84" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-1488" data-mp="1x3796wdqed78">
                Historial Clínico
              </h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-1489" data-mp="1if3y58ku0bbk">
              {isDoctor ? 'Mis historiales clínicos — Solo se muestran historiales creados por usted' : 'Gestión de historiales clínicos de pacientes'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-1490" data-mp="2g0i7oerjh2lm">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1491" data-mp="262kj1q5rp3sh">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1493" data-mp="pr846bmncxih" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-1492" data-mp="pom0in2vn25j">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1495" data-mp="21b8cmoxf9ai" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-1494" data-mp="1yyfvfljlgj7">
                Exportar Excel
              </Button>}
            {canCreate('clinicalHistory') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1497" data-mp="kwj6os7vap6g" />} onClick={openNewForm} className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg shadow-emerald-900/20" data-id="element-1496" data-mp="kxkhqwovvbo6">
                Nuevo Historial
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1498" data-mp="2ray7g9i6moc9">
          <LockIcon className="w-4 h-4" data-id="element-1499" data-mp="2wbm69hcnk9h1" />
          <span data-id="element-1500" data-mp="30abi35i1liyk">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            historiales clínicos.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-1501" data-mp="3a8hkrpboat4h">
        <div className="w-full md:w-96 relative" data-id="element-1502" data-mp="3f83u3nodrp80">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1503" data-mp="3f6sqpoyukjhf" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar por paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-1504" data-mp="3o0xqwyzvf79m" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-1505" data-mp="15dnbo8ns6gyd">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-1506" data-mp="10fvh16vr4gqh">
              <tab.icon className="w-4 h-4" data-id="element-1507" data-mp="3say9mejthzvw" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-1508" data-mp="3nioe8izxo7m8">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Histories List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-1509" data-mp="3dmtt9h88m0ga">
        <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-1510" data-mp="38lnx19fdbv20" />
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1511" data-mp="34ldr1wrkt4n5">
          {filteredHistories.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-1512" data-mp="2zo3vdzdlfwqv">
              <p className="text-sm" data-id="element-1513" data-mp="12spj5a3zlt11">No se encontraron historiales</p>
            </div> : paginatedHistories.map(history => {
          const patient = getPatient(history.patientId);
          return <div key={history.id} className="px-4 py-3 flex items-center gap-3" data-id="element-1514" data-mp="1lq9a454n3awb">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-1515" data-mp="17zw2k3vnty7i">
                    {(patient?.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1516" data-mp="24ovdjqnmpe3e">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate capitalize" data-id="element-1517" data-mp="24mhg5b5h7pso">
                      {patient?.name || '-'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-1518" data-mp="1vmcbv9vkx7iq">
                      {formatDate(history.date)} ·{' '}
                      {history.personalBackground?.consultationReason || '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1519" data-mp="cog31t80uorh">
                    <button onClick={() => openViewHistory(history)} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Ver" data-id="element-1520" data-mp="7q4pb7elvu3z">
                      <EyeIcon className="w-4 h-4" data-id="element-1521" data-mp="3p6cwl14o7cvx" />
                    </button>
                    {viewMode === 'active' && canEdit('clinicalHistory') && <button onClick={() => openEditHistory(history)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar" data-id="element-1522" data-mp="3l6vpy4dic9p6">
                        <EditIcon className="w-4 h-4" data-id="element-1523" data-mp="36ebmf2mg4077" />
                      </button>}
                  </div>
                </div>;
        })}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-1524" data-mp="3k2lrefvk9sw5">
          <table className="w-full" data-id="element-1525" data-mp="3k3549i6ctr3b">
            <thead data-id="element-1526" data-mp="3p2848o7h974f">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1527" data-mp="3p0x3yyd8qhi2">
                <SortableHeader label="Fecha" sortKey="date" currentDirection={chIsSorted('date')} onSort={chRequestSort} data-id="element-1528" data-mp="3ttgibxjr5gnx" />

                <SortableHeader label="Paciente" sortKey="patientId" currentDirection={chIsSorted('patientId')} onSort={chRequestSort} data-id="element-1529" data-mp="6me3ml7gdbi4" />

                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1530" data-mp="gjsi4f9tfq7s">
                  Motivo de Consulta
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1531" data-mp="2ifm65ro22tdh">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1532" data-mp="qq3apzoext4l">
              {filteredHistories.length === 0 ? <tr data-id="element-1533" data-mp="lrs1nr5xzmnf">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-1534" data-mp="lsjvoxjw5cbl">
                    <div className="flex flex-col items-center justify-center" data-id="element-1535" data-mp="1ef4lvuomridk">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-1536" data-mp="19gkz5s27q2k5">
                        <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-1537" data-mp="14i9mzaog3fyr" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-1538" data-mp="3u726h717v5yw">
                        No se encontraron historiales
                      </h3>
                      <p className="text-sm" data-id="element-1539" data-mp="3qp4gh1kzqw9l">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo historial' : 'No hay historiales en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedHistories.map(history => {
              const patient = getPatient(history.patientId);
              return <tr key={history.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-1540" data-mp="2yxyox9jc66vx">
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-1541" data-mp="2oziuawl3ofho">
                        {formatDate(history.date)}
                      </td>
                      <td className="py-4 px-6" data-id="element-1542" data-mp="2txu5ban4sw1h">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1543" data-mp="2acadng90vhyb">
                          {patient?.name}
                        </p>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400" data-id="element-1544" data-mp="2kazxvfoe5ehq">
                        {history.personalBackground.consultationReason || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-1545" data-mp="2fe9g8nwxuau7">
                        <div className="flex items-center justify-end gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-1546" data-mp="2tamrhfavpg08">
                          <button onClick={() => openViewHistory(history)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Ver historial" data-id="element-1547" data-mp="331ho9dlr3xzx">
                            <EyeIcon className="w-4 h-4" data-id="element-1548" data-mp="381dgygp00306" />
                          </button>
                          {viewMode === 'active' && !readOnly && <button onClick={() => openEditHistory(history)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar historial" data-id="element-1549" data-mp="ici9vrr628zn">
                              <EditIcon className="w-4 h-4" data-id="element-1550" data-mp="3vq3kn79m7io2" />
                            </button>}
                          <button onClick={() => handleDownloadPdf(history)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-1551" data-mp="4j99ggl74xt9">
                            <FileDownIcon className="w-4 h-4" data-id="element-1552" data-mp="3h04p7c9g2lca" />
                          </button>

                          {viewMode === 'active' ? <>
                              <button onClick={() => openActionConfirm(history, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-1553" data-mp="39e98nkhym95j">
                                <ArchiveIcon className="w-4 h-4" data-id="element-1554" data-mp="2lqiyxinptfki" />
                              </button>
                              <button onClick={() => openActionConfirm(history, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-1555" data-mp="2grg0wi65k9m1">
                                <PowerOffIcon className="w-4 h-4" data-id="element-1556" data-mp="1bp2j8j5gnybe" />
                              </button>
                            </> : <button onClick={() => restoreItem(history.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-1557" data-mp="1lp3263zf4cf2">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-1558" data-mp="o2dr74mqmph2" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <Pagination currentPage={chCurrentPage} totalPages={chTotalPages} totalItems={chTotalItems} startIndex={chStartIndex} endIndex={chEndIndex} onPageChange={chGoToPage} onNext={chNextPage} onPrev={chPrevPage} data-id="element-1559" data-mp="5akyzcfht8ae" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nuevo Historial Clínico" size="full" icon={<FileTextIcon className="w-6 h-6 text-white" data-id="element-1561" data-mp="2bwhbetlgnbxn" />} accentColor="emerald" data-id="element-1560" data-mp="27xi7ncbgtl7s">
        <div className="modal-content-scrollable" data-id="element-1562" data-mp="2zoxrwzpjuvuu">
          <form onSubmit={handleSubmit} className="space-y-8 pb-8" data-id="element-1563" data-mp="34nr5st8r0ovj">
            {saveError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-1564" data-mp="2qnzqmp6hx2fy">
                ❌ {saveError}
              </div>}

            {/* Selección de Paciente */}
            <FormSection title="Paciente" data-id="element-1565" data-mp="1fansmuxf3e1p">
              <div className="grid grid-cols-1 gap-6" data-id="element-1566" data-mp="1k7nwcz6so4pn">
                <div data-field="patient" data-id="element-1567" data-mp="1k51gmc9r2iwc">
                  <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={handlePatientSelect} error={formErrors.patient} searchable placeholder="Buscar paciente..." data-id="element-1568" data-mp="3lwtkx6upu8gx" />
                </div>

                {/* Retomar Historial Banner */}
                {selectedPatientId && previousHistoryForPatient && !retakeApplied && <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl" data-id="element-1569" data-mp="2t7wdxzvyfy3h">
                      <div className="flex items-start gap-3" data-id="element-1570" data-mp="2p7ce2ojzygdn">
                        <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg flex-shrink-0" data-id="element-1571" data-mp="rg9gpglgz6yk">
                          <ClockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-1572" data-mp="nf7h2ynn4sw2" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-1573" data-mp="25bhutcbg3fbf">
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-1574" data-mp="2abnqr4oqznzb">
                            Este paciente tiene un historial previo
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5" data-id="element-1575" data-mp="2f4qdotz0rz6j">
                            Última visita:{' '}
                            <span className="font-medium" data-id="element-1576" data-mp="1meqf1cv43ic4">
                              {formatDate(previousHistoryForPatient.date)}
                            </span>
                            {previousHistoryForPatient.personalBackground.consultationReason && <span className="ml-1" data-id="element-1577" data-mp="20ej1gvzlvcea">
                                —{' '}
                                {previousHistoryForPatient.personalBackground.consultationReason}
                              </span>}
                          </p>
                          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5" data-id="element-1578" data-mp="35d67cenywxji">
                            ¿Deseas retomar los antecedentes? Se pre-llenarán:{' '}
                            <span className="font-medium" data-id="element-1579" data-mp="3faso76otuz64">
                              datos personales, antecedentes clínicos, revisión
                              por sistemas
                            </span>
                            . El motivo de consulta, diagnóstico y tratamiento
                            quedarán en blanco para esta nueva visita.
                          </p>
                          <div className="flex items-center gap-2 mt-3" data-id="element-1580" data-mp="1vo30h7em8nu">
                            <button type="button" onClick={handleRetakeHistory} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-1581" data-mp="1xqznk32b01j">
                              <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-1582" data-mp="zhh5pb6inldz" />
                              Retomar Antecedentes
                            </button>
                            <button type="button" onClick={() => setPreviousHistoryForPatient(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-lg transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30" data-id="element-1583" data-mp="ljsgxs9bvu3n">
                              Comenzar desde cero
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>}

                {/* Retake applied confirmation */}
                {selectedPatientId && retakeApplied && <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center gap-3" data-id="element-1584" data-mp="1zivs8pxzuwgd">
                    <div className="p-1.5 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg flex-shrink-0" data-id="element-1585" data-mp="1prr8lcxdinf7">
                      <CheckIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" data-id="element-1586" data-mp="1lrh464p8c52g" />
                    </div>
                    <div className="flex-1" data-id="element-1587" data-mp="2idj261wvbkcr">
                      <p className="text-sm text-emerald-700 dark:text-emerald-300" data-id="element-1588" data-mp="2dhvddnmast7s">
                        <span className="font-medium" data-id="element-1589" data-mp="29ep7ih33l41u">
                          Antecedentes retomados
                        </span>{' '}
                        del historial del{' '}
                        {previousHistoryForPatient ? formatDate(previousHistoryForPatient.date) : ''}
                        . Revisa y actualiza los campos según corresponda.
                      </p>
                    </div>
                    <button type="button" onClick={() => {
                  setRetakeApplied(false);
                  // Reset to fresh patient data (undo the retake)
                  const patient = getPatient(selectedPatientId);
                  if (patient) {
                    const isMale = patient.gender?.toLowerCase() === 'masculino';
                    setPersonalData({
                      fullName: patient.name,
                      birthDate: '',
                      address: patient.address,
                      phone: patient.phone,
                      email: patient.email,
                      age: 0,
                      gender: patient.gender,
                      occupation: '',
                      numberOfChildren: 0,
                      deliveryType: isMale ? 'N/A' : '',
                      alcohol: '',
                      tobacco: '',
                      drugs: '',
                      infusions: '',
                      consultationReason: ''
                    });
                  }
                  setClinicalData({
                    diseases: '',
                    currentMedications: '',
                    chronicDiseases: '',
                    surgicalHistory: '',
                    implantsOrDevices: '',
                    implantType: '',
                    allergies: ''
                  });
                  setPhysicalExamData({
                    bloodPressure: '',
                    bloodSugar: '',
                    heartRate: '',
                    lungs: '',
                    eyes: '',
                    throat: '',
                    abdomen: '',
                    weight: '',
                    height: '',
                    temperature: '',
                    positiveAspects: '',
                    respiratoryRate: '',
                    weightLb: ''
                  });
                  setDiseaseHistoryData({
                    sinceWhen: '',
                    previousTreatment: '',
                    evolution: ''
                  });
                  setSystemsReviewData({
                    general: '',
                    cardioRespiratory: '',
                    gastrointestinal: '',
                    genitourinary: '',
                    osteoMuscular: '',
                    neurological: '',
                    skinAndAppendages: ''
                  });
                }} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex-shrink-0" data-id="element-1590" data-mp="34mhhe3oewb8f">
                      Deshacer
                    </button>
                  </div>}

                {/* Show auto-assigned doctor info */}
                {(currentUserDoctor || user) && <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-1591" data-mp="1q8ac7b8218i7">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300" data-id="element-1592" data-mp="1ba8a75go8v32">
                      <span className="font-medium" data-id="element-1593" data-mp="1gacp4r1xlhr6">
                        Doctor asignado automáticamente:
                      </span>{' '}
                      {currentUserDoctor ? `${getDoctorPrefix(currentUserDoctor.gender, getSpecialty(currentUserDoctor.specialtyId)?.name)} ${currentUserDoctor.name}` : user?.name || ''}
                    </p>
                  </div>}
              </div>
            </FormSection>

            {/* Antecedentes Personales */}
            <FormSection title="Antecedentes Personales" data-id="element-1594" data-mp="3brm6pqk6kr2k">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1595" data-mp="36t2j7lpxv783">
                <div data-field="fullName" data-id="element-1596" data-mp="3kjo3nev1jp83">
                  <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
                  ...personalData,
                  fullName: e.target.value
                })} error={formErrors.fullName} readOnly={!!selectedPatientId} className={selectedPatientId ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1597" data-mp="3kkppmedlhw4l" />
                </div>
                <Input type="date" label="Fecha de Nacimiento (F.N.)" value={personalData.birthDate} onChange={e => {
                const newBirthDate = e.target.value;
                let calculatedAge = 0;
                if (newBirthDate) {
                  const birth = new Date(newBirthDate);
                  const now = new Date();
                  calculatedAge = now.getFullYear() - birth.getFullYear();
                  const m = now.getMonth() - birth.getMonth();
                  if (m < 0 || m === 0 && now.getDate() < birth.getDate()) {
                    calculatedAge--;
                  }
                  if (calculatedAge < 0) calculatedAge = 0;
                }
                setPersonalData({
                  ...personalData,
                  birthDate: newBirthDate,
                  age: calculatedAge
                });
              }} max={today} data-id="element-1598" data-mp="295almqmyy3zz" />

                <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
                ...personalData,
                address: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.address} className={selectedPatientId && personalData.address ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1599" data-mp="1eodr99g3jrsd" />

                <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
                ...personalData,
                phone: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.phone} className={selectedPatientId && personalData.phone ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1600" data-mp="3e01721yowibp" />

                <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
                ...personalData,
                email: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.email} className={selectedPatientId && personalData.email ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1601" data-mp="3le0ek0lz7pma" />

                <Input type="number" label="Edad" value={personalData.age} readOnly className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed" helperText={personalData.birthDate ? 'Calculada automáticamente' : 'Se calcula al ingresar fecha de nacimiento'} data-id="element-1602" data-mp="myhah45uvhjc" />

                <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
                ...personalData,
                gender: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.gender} className={selectedPatientId && personalData.gender ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1603" data-mp="2l751kz48smad" />

                <Input label="Ocupación u Oficio" value={personalData.occupation} onChange={e => setPersonalData({
                ...personalData,
                occupation: e.target.value
              })} data-id="element-1604" data-mp="3dvaq7wyf2vfh" />

                <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
                ...personalData,
                numberOfChildren: parseInt(e.target.value) || 0
              })} data-id="element-1605" data-mp="2fkrjh5uzxld0" />

                <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
                ...personalData,
                deliveryType: e.target.value
              })} placeholder="Ej: C/A, Normal" readOnly={personalData.gender?.toLowerCase() === 'masculino'} className={personalData.gender?.toLowerCase() === 'masculino' ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-1606" data-mp="1rsu6hbft8nab" />

                <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
                ...personalData,
                alcohol: e.target.value
              })} data-id="element-1607" data-mp="rl6zh3k4nua8" />

                <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
                ...personalData,
                tobacco: e.target.value
              })} data-id="element-1608" data-mp="dtsfatehhn1l" />

                <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
                ...personalData,
                drugs: e.target.value
              })} data-id="element-1609" data-mp="3r1lrgohur59f" />

                <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
                ...personalData,
                infusions: e.target.value
              })} data-id="element-1610" data-mp="19rpjlju6x7eu" />

                <div className="md:col-span-3" data-id="element-1611" data-mp="14uz5v35uynwl">
                  <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                  ...personalData,
                  consultationReason: e.target.value
                })} rows={2} data-id="element-1612" data-mp="q59z8sezxrox" />
                </div>
              </div>
            </FormSection>

            {/* Antecedentes Clínicos */}
            <FormSection title="Antecedentes Clínicos" data-id="element-1613" data-mp="3topmwzq59da6">
              <div className="grid grid-cols-1 gap-4" data-id="element-1614" data-mp="bi2z7hr1ue4t">
                <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
                ...clinicalData,
                diseases: e.target.value
              })} rows={2} data-id="element-1615" data-mp="bjmeb50yyj50" />

                <Textarea label="Medicamentos que Toma Actualmente" value={clinicalData.currentMedications} onChange={e => setClinicalData({
                ...clinicalData,
                currentMedications: e.target.value
              })} rows={2} data-id="element-1616" data-mp="v6k0r9pib7ar" />

                <Textarea label="Antecedentes Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
                ...clinicalData,
                chronicDiseases: e.target.value
              })} rows={2} data-id="element-1617" data-mp="21kd81zmkwslr" />

                <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
                ...clinicalData,
                surgicalHistory: e.target.value
              })} rows={2} data-id="element-1618" data-mp="1ilhjp4xej8fu" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1619" data-mp="ywqkr4bkl5u6">
                  <Input label="Uso de Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantsOrDevices: e.target.value
                })} data-id="element-1620" data-mp="yyt97jx2qqws" />

                  <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantType: e.target.value
                })} data-id="element-1621" data-mp="3fhc3dh02n5hu" />
                </div>
                <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
                ...clinicalData,
                allergies: e.target.value
              })} rows={2} data-id="element-1622" data-mp="2ws5gdhs8vrq8" />
              </div>
            </FormSection>

            {/* Exploración Física */}
            <FormSection title="Exploración Física" data-id="element-1623" data-mp="210al16cxkvqd">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1624" data-mp="1x0a5tji0cgi9">
                <Input label="Presión Arterial (T.A.)" value={physicalExamData.bloodPressure} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodPressure: e.target.value
              })} placeholder="120/80 mmHg" data-id="element-1625" data-mp="2asq281gelga7" />

                <Input label="Azúcar en Sangre" value={physicalExamData.bloodSugar} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodSugar: e.target.value
              })} placeholder="mg/dL" data-id="element-1626" data-mp="1s30537p64if4" />

                <Input label="Frec. Cardíaca (F.C.)" value={physicalExamData.heartRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                heartRate: e.target.value
              })} placeholder="Vm" data-id="element-1627" data-mp="ka06z3gntkrp" />

                <Input label="Frec. Respiratoria (F.R.)" value={physicalExamData.respiratoryRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                respiratoryRate: e.target.value
              })} placeholder="r/m" data-id="element-1628" data-mp="13xqze6wc0uej" />

                <Input label="Peso (Kg)" value={physicalExamData.weight} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weight: e.target.value
              })} placeholder="kg" data-id="element-1629" data-mp="1mwv08sdpzhv4" />

                <Input label="Peso (Lb)" value={physicalExamData.weightLb} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weightLb: e.target.value
              })} placeholder="lb" data-id="element-1630" data-mp="3q6gj6zjmub76" />

                <Input label="Altura" value={physicalExamData.height} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                height: e.target.value
              })} placeholder="cm" data-id="element-1631" data-mp="ht15xgu4xi8r" />

                <Input label="Temperatura" value={physicalExamData.temperature} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                temperature: e.target.value
              })} placeholder="°C" data-id="element-1632" data-mp="10p0sgc4cftky" />

                <Input label="Pulmones" value={physicalExamData.lungs} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                lungs: e.target.value
              })} data-id="element-1633" data-mp="1m1rj1ji2cswv" />

                <Input label="Ojos" value={physicalExamData.eyes} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                eyes: e.target.value
              })} data-id="element-1634" data-mp="20qe7capo4v08" />

                <Input label="Garganta" value={physicalExamData.throat} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                throat: e.target.value
              })} data-id="element-1635" data-mp="en4dt7g3e12d" />

                <Input label="Abdomen" value={physicalExamData.abdomen} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                abdomen: e.target.value
              })} data-id="element-1636" data-mp="jl65uhvhxapz" />

                <div className="col-span-2 sm:col-span-3 md:col-span-4" data-id="element-1637" data-mp="179pe4xejpni2">
                  <Textarea label="Aspectos Positivos" value={physicalExamData.positiveAspects} onChange={e => setPhysicalExamData({
                  ...physicalExamData,
                  positiveAspects: e.target.value
                })} rows={2} placeholder="Aspectos positivos del examen físico..." data-id="element-1638" data-mp="12e0aoizenmgh" />
                </div>
              </div>
            </FormSection>

            {/* Historia de la Enfermedad Actual */}
            <FormSection title="Historia de la Enfermedad Actual" data-id="element-1639" data-mp="1etrnn8ri9dga">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1640" data-mp="10va1hfh9090h">
                <Input label="¿Desde cuándo padece el problema?" value={diseaseHistoryData.sinceWhen} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                sinceWhen: e.target.value
              })} placeholder="Ej: 3 meses, 1 año..." data-id="element-1641" data-mp="15umpi3juvm4n" />

                <Input label="¿Se ha tratado con anterioridad?" value={diseaseHistoryData.previousTreatment} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                previousTreatment: e.target.value
              })} placeholder="Sí / No / Descripción..." data-id="element-1642" data-mp="2qares1249m8q" />

                <div className="md:col-span-2" data-id="element-1643" data-mp="za5i6g2ewgfo">
                  <Textarea label="Evolución" value={diseaseHistoryData.evolution} onChange={e => setDiseaseHistoryData({
                  ...diseaseHistoryData,
                  evolution: e.target.value
                })} rows={4} placeholder="Descripción de la evolución de la enfermedad actual..." data-id="element-1644" data-mp="vawp7df74ml6" />
                </div>
              </div>
            </FormSection>

            {/* Revisión por Sistemas */}
            <FormSection title="Revisión por Sistemas" data-id="element-1645" data-mp="2j8xdcgrxdu12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1646" data-mp="2j6b8wq0bzun2">
                <Input label="General" value={systemsReviewData.general} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                general: e.target.value
              })} data-id="element-1647" data-mp="2n8obj5js7cfm" />

                <Input label="Cardio-Respiratorio" value={systemsReviewData.cardioRespiratory} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                cardioRespiratory: e.target.value
              })} data-id="element-1648" data-mp="20bw60pawfhty" />

                <Input label="Gastro-Intestinal" value={systemsReviewData.gastrointestinal} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                gastrointestinal: e.target.value
              })} data-id="element-1649" data-mp="3kp50h7clz9dt" />

                <Input label="Genito-Urinario" value={systemsReviewData.genitourinary} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                genitourinary: e.target.value
              })} data-id="element-1650" data-mp="277o1roicbee5" />

                <Input label="Osteo-Muscular" value={systemsReviewData.osteoMuscular} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                osteoMuscular: e.target.value
              })} data-id="element-1651" data-mp="2pz72foow6gd0" />

                <Input label="Neurológico" value={systemsReviewData.neurological} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                neurological: e.target.value
              })} data-id="element-1652" data-mp="10kshzgkwpfnb" />

                <Input label="Piel & Anexos" value={systemsReviewData.skinAndAppendages} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                skinAndAppendages: e.target.value
              })} data-id="element-1653" data-mp="ec3kyg861rms" />
              </div>
            </FormSection>

            {/* Diagnóstico */}
            <FormSection title="Diagnóstico" data-id="element-1654" data-mp="x1tdsxsdc3c2">
              <Textarea value={diagnosis} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.diagnosis) setDiagnosis(e.target.value);
            }} rows={6} placeholder="Escriba el diagnóstico..." maxLength={CHAR_LIMITS.diagnosis} data-id="element-1655" data-mp="3mrn6su6ic7rt" />

              <p className={`mt-1 text-xs text-right ${diagnosis.length >= CHAR_LIMITS.diagnosis ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1656" data-mp="2dsm1cz0bbp0l">
                {diagnosis.length}/{CHAR_LIMITS.diagnosis} caracteres
              </p>
            </FormSection>

            {/* Tratamiento */}
            <FormSection title="Tratamiento" data-id="element-1657" data-mp="1uvm0ikdpdgf0">
              <Textarea value={treatment} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.treatment) setTreatment(e.target.value);
            }} rows={10} placeholder="Escriba el tratamiento detallado..." maxLength={CHAR_LIMITS.treatment} data-id="element-1658" data-mp="1uxf8c301y6ol" />

              <p className={`mt-1 text-xs text-right ${treatment.length >= CHAR_LIMITS.treatment ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1659" data-mp="1c41kehurb0ih">
                {treatment.length}/{CHAR_LIMITS.treatment} caracteres
              </p>
            </FormSection>

            {/* Indicaciones Médicas */}
            <FormSection title="Indicaciones Médicas" data-id="element-1660" data-mp="12fnyn1tx7hzl">
              <Textarea value={medicalIndications} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.medicalIndications) setMedicalIndications(e.target.value);
            }} rows={6} placeholder="Escriba las indicaciones médicas para el paciente..." maxLength={CHAR_LIMITS.medicalIndications} data-id="element-1661" data-mp="xhmc26xad0jd" />

              <p className={`mt-1 text-xs text-right ${medicalIndications.length >= CHAR_LIMITS.medicalIndications ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1662" data-mp="1gb8c3hqvr7zm">
                {medicalIndications.length}/{CHAR_LIMITS.medicalIndications}{' '}
                caracteres
              </p>
            </FormSection>

            {/* Sesiones */}
            <FormSection title="Sesiones" data-id="element-1663" data-mp="3onk7vaifg8yh">
              <div className="space-y-4" data-id="element-1664" data-mp="3tg52ge95qq32">
                {/* Multi-select services for sessions */}
                <div data-id="element-1665" data-mp="29anrdlj5n3i">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1666" data-mp="28irs1o0tncp">
                    Servicios / Tipo de Sesión
                  </label>

                  {/* Search input */}
                  <div className="relative" data-id="element-1667" data-mp="35r2bgfsmvkvw">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1668" data-mp="3apdk4n76zpcj" />
                    <input type="text" value={sessionServiceSearch} onChange={e => setSessionServiceSearch(e.target.value)} placeholder="Buscar servicios..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-1669" data-mp="3foqia43ikiuf" />
                  </div>

                  {/* Service list with checkboxes */}
                  <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-1670" data-mp="20rno0q7ie2je">
                    {filteredSessionServices.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-1671" data-mp="1rqfx4t7bo3zc">
                        No se encontraron servicios
                      </div> : filteredSessionServices.map(svc => {
                    const isSelected = selectedSessionServiceIds.includes(svc.id);
                    return <label key={svc.id} className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-1672" data-mp="3c7c7v8pgulfz">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSessionService(svc.id)} className="w-3.5 h-3.5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500" data-id="element-1673" data-mp="15jbvtlsmu4y9" />

                            <span className="text-sm text-gray-900 dark:text-white flex-1" data-id="element-1674" data-mp="1o8rwqa88bzf7">
                              {svc.name}
                            </span>
                            <span className="text-xs text-gray-400" data-id="element-1675" data-mp="7t74zhegf4xs">
                              {svc.duration} min
                            </span>
                          </label>;
                  })}
                  </div>
                </div>

                {/* Per-service session numbers */}
                {selectedSessionServiceIds.length > 0 && <div data-id="element-1676" data-mp="2zzy9e8ntigy3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1677" data-mp="2w0qkydbgkgmh">
                      N° de Sesión por Servicio
                    </label>
                    <div className="space-y-2" data-id="element-1678" data-mp="288iezsg0rpxb">
                      {selectedSessionServiceIds.map(sId => {
                    const svc = services.find(s => s.id === sId);
                    if (!svc) return null;
                    return <div key={sId} className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-1679" data-mp="2cawymeriitpn">
                            <div className="flex-1 min-w-0" data-id="element-1680" data-mp="29gmdc5opkbyo">
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-1681" data-mp="29i5tzxea0p0z">
                                {svc.name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1682" data-mp="2iivqz4f9wlga">
                                {svc.duration} min
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1683" data-mp="2sd4dnbhmhk6j">
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-1684" data-mp="2xbxjchqo46w5">
                                Sesión N°
                              </span>
                              <div className="flex items-center" data-id="element-1685" data-mp="365kb7wrr8q02">
                                <input type="number" min="1" step="1" value={sessionServiceNumbers[sId] || '1'} onChange={e => setSessionServiceNumbers(prev => ({
                            ...prev,
                            [sId]: e.target.value
                          }))} className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-id="element-1686" data-mp="3b683elrpzcvv" />

                                <div className="flex flex-col border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg overflow-hidden" data-id="element-1687" data-mp="vk1luofkqc4y">
                                  <button type="button" onClick={() => setSessionServiceNumbers(prev => ({
                              ...prev,
                              [sId]: String((parseInt(prev[sId] || '1') || 0) + 1)
                            }))} className="px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors border-b border-gray-300 dark:border-gray-600" data-id="element-1688" data-mp="qsjkhz9n29jg">
                                    <ChevronUpIcon className="w-3 h-3" data-id="element-1689" data-mp="2g8udaf3keaum" />
                                  </button>
                                  <button type="button" onClick={() => setSessionServiceNumbers(prev => ({
                              ...prev,
                              [sId]: String(Math.max(1, (parseInt(prev[sId] || '2') || 2) - 1))
                            }))} className="px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors" data-id="element-1690" data-mp="2u83ot1gtsgs9">
                                    <ChevronDownIcon className="w-3 h-3" data-id="element-1691" data-mp="274xbd8ibi1zz" />
                                  </button>
                                </div>
                              </div>
                              <button type="button" onClick={() => toggleSessionService(sId)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Quitar servicio" data-id="element-1692" data-mp="2c5utjl3buw1h">
                                <XIcon className="w-4 h-4" data-id="element-1693" data-mp="3uakednegigr" />
                              </button>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>}
              </div>
            </FormSection>

            {/* Comentarios */}
            <FormSection title="Comentarios" data-id="element-1694" data-mp="mltjwai7tee1">
              <Textarea value={comments} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.comments) setComments(e.target.value);
            }} rows={3} placeholder="Comentarios adicionales..." maxLength={CHAR_LIMITS.comments} data-id="element-1695" data-mp="mp8zrfjpqrgi" />

              <p className={`mt-1 text-xs text-right ${comments.length >= CHAR_LIMITS.comments ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1696" data-mp="328ry7lzp2iwt">
                {comments.length}/{CHAR_LIMITS.comments} caracteres
              </p>
            </FormSection>

            {/* Archivos Adjuntos */}
            <FormSection title="Archivos Adjuntos" data-id="element-1697" data-mp="3g20ymx5nzu37">
              <div className="space-y-4" data-id="element-1698" data-mp="3kxgchp3o9pzm">
                <input ref={imageInputRef} type="file" accept={ATTACH_ACCEPT} multiple onChange={handleImageAttach} className="hidden" data-id="element-1699" data-mp="3l0kq1d311y2y" />

                <button type="button" onClick={() => imageInputRef.current?.click()} className="flex items-center gap-3 w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400" data-id="element-1700" data-mp="1vl4pgm365koo">
                  <ImageIcon className="w-6 h-6" data-id="element-1701" data-mp="3v1db9w2m36k4" />
                  <div className="text-left" data-id="element-1702" data-mp="3uzirwm6znmi5">
                    <p className="text-sm font-medium" data-id="element-1703" data-mp="37b9adwgt5kwt">
                      Adjuntar Imágenes o Documentos
                    </p>
                    <p className="text-xs" data-id="element-1704" data-mp="33ca5ucyznq5w">
                      Imágenes, PDF, Word, Excel y más (máx. 10MB cada uno)
                    </p>
                  </div>
                </button>

                {/* Attachment previews */}
                {attachedImages.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1705" data-mp="2ok87wyghb78i">
                    {attachedImages.map((img, index) => isImageAttachment(img) ? <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1706" data-mp="z3m9gflx6eh7">
                          <img src={img.data} alt={img.name} className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setViewingImage(img.data)} data-id="element-1707" data-mp="q1mqviv9b2c5" />

                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2" data-id="element-1708" data-mp="18uqkx5cdzh7a">
                            <p className="text-xs text-white truncate" data-id="element-1709" data-mp="1ds8t2tikxse8">
                              {img.name}
                            </p>
                          </div>
                          <button type="button" onClick={() => setAttachedImages(prev => prev.filter((_, i) => i !== index))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600" data-id="element-1710" data-mp="2eod4syiu8hx">
                            <XIcon className="w-3 h-3" data-id="element-1711" data-mp="2vrzrzlqkbpg0" />
                          </button>
                        </div> : <div key={index} className="relative group rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center h-48 p-4" data-id="element-1712" data-mp="2qu6pb2ogxdkh">
                          <a href={img.data} download={img.name} className="flex flex-col items-center justify-center gap-2 text-center w-full" data-id="element-1713" data-mp="kwsw1l3uk5rp">
                            <FileTextIcon className="w-10 h-10 text-emerald-500" data-id="element-1714" data-mp="shd26u7dsbyd" />
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate w-full px-1" data-id="element-1715" data-mp="x8v17cpug2gp">
                              {img.name}
                            </p>
                            <span className="text-[11px] text-emerald-600 dark:text-emerald-400" data-id="element-1716" data-mp="4kz6gdju4kmk">
                              Descargar
                            </span>
                          </a>
                          <button type="button" onClick={() => setAttachedImages(prev => prev.filter((_, i) => i !== index))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600" data-id="element-1717" data-mp="ei3uyk15ks6j">
                            <XIcon className="w-3 h-3" data-id="element-1718" data-mp="2b2vzjymnhr0m" />
                          </button>
                        </div>)}
                  </div>}
              </div>
            </FormSection>

            {/* Firma del Doctor */}
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1719" data-mp="2yqmbm74dawot">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-1720" data-mp="2trj50i8qoieq">
                Firma del Dr. (a)
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-1721" data-mp="38nik4cu9nitd">
                {currentUserDoctor ? `${getDoctorPrefix(currentUserDoctor.gender, getSpecialty(currentUserDoctor.specialtyId)?.name)} ${currentUserDoctor.name}` : user?.name || 'Usuario actual'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1722" data-mp="3l02v9ssq983h">
                Fecha: {formatDate(new Date())}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 p-4 -mx-4 -mb-4 shadow-lg" data-id="element-1723" data-mp="3ba142s52v9kc">
              <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1724" data-mp="3g79pr2pfpvqc">
                Cancelar
              </Button>
              <Button type="submit" isLoading={isSaving} data-id="element-1725" data-mp="36c9a7skvhddi">
                {isSaving ? 'Guardando...' : 'Guardar Historial Clínico'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Clinical History Modal */}
      <Modal isOpen={isEditFormOpen} onClose={() => {
      setIsEditFormOpen(false);
      setEditingHistory(null);
      resetForm();
    }} title="Editar Historial Clínico" size="full" icon={<EditIcon className="w-6 h-6 text-white" data-id="element-1727" data-mp="zffv3g8kpn9r" />} accentColor="emerald" data-id="element-1726" data-mp="qjit0831kgnr">
        <div className="modal-content-scrollable" data-id="element-1728" data-mp="ultnw3l6iblc">
          <form onSubmit={handleEditSubmit} className="space-y-8 pb-8" data-id="element-1729" data-mp="pns8bsmyouem">
            {saveError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-1730" data-mp="kpqp97pjcv35">
                ❌ {saveError}
              </div>}

            {/* Antecedentes Personales */}
            <FormSection title="Antecedentes Personales" data-id="element-1731" data-mp="1dc1p2g5biryw">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1732" data-mp="18di2cdiwhc5h">
                <div data-field="fullName" data-id="element-1733" data-mp="13g83k79mfo4z">
                  <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
                  ...personalData,
                  fullName: e.target.value
                })} error={formErrors.fullName} data-id="element-1734" data-mp="13f6vmbnwjhrh" />
                </div>
                <Input type="date" label="Fecha de Nacimiento" value={personalData.birthDate} onChange={e => {
                const nb = e.target.value;
                let age = 0;
                if (nb) {
                  const b = new Date(nb);
                  const n = new Date();
                  age = n.getFullYear() - b.getFullYear();
                  const m = n.getMonth() - b.getMonth();
                  if (m < 0 || m === 0 && n.getDate() < b.getDate()) age--;
                  if (age < 0) age = 0;
                }
                setPersonalData({
                  ...personalData,
                  birthDate: nb,
                  age
                });
              }} max={today} data-id="element-1735" data-mp="28kn4j0duuim1" />

                <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
                ...personalData,
                address: e.target.value
              })} data-id="element-1736" data-mp="yx7y2fp208pb" />

                <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
                ...personalData,
                phone: e.target.value
              })} data-id="element-1737" data-mp="1hvsnm3bar60u" />

                <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
                ...personalData,
                email: e.target.value
              })} data-id="element-1738" data-mp="1mm91foj14tnq" />

                <Input type="number" label="Edad" value={personalData.age} readOnly className="bg-gray-100 dark:bg-gray-800" data-id="element-1739" data-mp="2g4dxq358xgu7" />

                <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
                ...personalData,
                gender: e.target.value
              })} data-id="element-1740" data-mp="2b75h46zqxkv2" />

                <Input label="Ocupación" value={personalData.occupation} onChange={e => setPersonalData({
                ...personalData,
                occupation: e.target.value
              })} data-id="element-1741" data-mp="1nim8fqcsb62g" />

                <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
                ...personalData,
                numberOfChildren: parseInt(e.target.value) || 0
              })} data-id="element-1742" data-mp="1ihyg91ctkj6n" />

                <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
                ...personalData,
                deliveryType: e.target.value
              })} placeholder="Ej: C/A, Normal" data-id="element-1743" data-mp="2747yb0w9enfm" />

                <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
                ...personalData,
                alcohol: e.target.value
              })} data-id="element-1744" data-mp="1o76rdaj5ad91" />

                <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
                ...personalData,
                tobacco: e.target.value
              })} data-id="element-1745" data-mp="3q1coe7bbj2y0" />

                <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
                ...personalData,
                drugs: e.target.value
              })} data-id="element-1746" data-mp="374l1lnwki5p7" />

                <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
                ...personalData,
                infusions: e.target.value
              })} data-id="element-1747" data-mp="3m23wz93r3h5m" />

                <div className="md:col-span-3" data-id="element-1748" data-mp="2xd6gp3iv2gv4">
                  <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                  ...personalData,
                  consultationReason: e.target.value
                })} rows={2} data-id="element-1749" data-mp="2sfwv5yylepck" />
                </div>
              </div>
            </FormSection>

            {/* Antecedentes Clínicos */}
            <FormSection title="Antecedentes Clínicos" data-id="element-1750" data-mp="2ngto68z1y91y">
              <div className="grid grid-cols-1 gap-4" data-id="element-1751" data-mp="2nielg6cr463i">
                <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
                ...clinicalData,
                diseases: e.target.value
              })} rows={2} data-id="element-1752" data-mp="2iosf0vx6kyg5" />

                <Textarea label="Medicamentos Actuales" value={clinicalData.currentMedications} onChange={e => setClinicalData({
                ...clinicalData,
                currentMedications: e.target.value
              })} rows={2} data-id="element-1753" data-mp="1ll770vu48o7a" />

                <Textarea label="Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
                ...clinicalData,
                chronicDiseases: e.target.value
              })} rows={2} data-id="element-1754" data-mp="24kbbrsemjvt5" />

                <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
                ...clinicalData,
                surgicalHistory: e.target.value
              })} rows={2} data-id="element-1755" data-mp="ea8fjs7lz1qv" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1756" data-mp="3mnxkl2ztrdov">
                  <Input label="Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantsOrDevices: e.target.value
                })} data-id="element-1757" data-mp="3indps62q4m5w" />

                  <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantType: e.target.value
                })} data-id="element-1758" data-mp="5gbzgu35hnb5" />
                </div>
                <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
                ...clinicalData,
                allergies: e.target.value
              })} rows={2} data-id="element-1759" data-mp="2ksjjfqmb5e9m" />
              </div>
            </FormSection>

            {/* Exploración Física */}
            <FormSection title="Exploración Física" data-id="element-1760" data-mp="33q47wwl5uatp">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1761" data-mp="38i3za0n5zveo">
                <Input label="Presión Arterial (T.A.)" value={physicalExamData.bloodPressure} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodPressure: e.target.value
              })} placeholder="120/80 mmHg" data-id="element-1762" data-mp="38khv4bpo5bna" />

                <Input label="Azúcar en Sangre" value={physicalExamData.bloodSugar} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodSugar: e.target.value
              })} placeholder="mg/dL" data-id="element-1763" data-mp="vmq8wubojs60" />

                <Input label="Frec. Cardíaca (F.C.)" value={physicalExamData.heartRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                heartRate: e.target.value
              })} placeholder="Vm" data-id="element-1764" data-mp="1ae0a2uouw7bj" />

                <Input label="Frec. Respiratoria (F.R.)" value={physicalExamData.respiratoryRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                respiratoryRate: e.target.value
              })} placeholder="r/m" data-id="element-1765" data-mp="1t8famoqtg81h" />

                <Input label="Peso (Kg)" value={physicalExamData.weight} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weight: e.target.value
              })} placeholder="kg" data-id="element-1766" data-mp="2h7obgw2yfxj9" />

                <Input label="Peso (Lb)" value={physicalExamData.weightLb} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weightLb: e.target.value
              })} placeholder="lb" data-id="element-1767" data-mp="1ygx4g16fwj81" />

                <Input label="Altura" value={physicalExamData.height} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                height: e.target.value
              })} placeholder="cm" data-id="element-1768" data-mp="3cv2fvxzr8tyx" />

                <Input label="Temperatura" value={physicalExamData.temperature} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                temperature: e.target.value
              })} placeholder="°C" data-id="element-1769" data-mp="2t8ehhbcpc2w8" />

                <Input label="Pulmones" value={physicalExamData.lungs} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                lungs: e.target.value
              })} data-id="element-1770" data-mp="37yu1wnxan6tc" />

                <Input label="Ojos" value={physicalExamData.eyes} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                eyes: e.target.value
              })} data-id="element-1771" data-mp="22xujj7ly03ig" />

                <Input label="Garganta" value={physicalExamData.throat} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                throat: e.target.value
              })} data-id="element-1772" data-mp="1ozw8oc6typ7p" />

                <Input label="Abdomen" value={physicalExamData.abdomen} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                abdomen: e.target.value
              })} data-id="element-1773" data-mp="1a7m1rgvbjl4e" />

                <div className="col-span-2 sm:col-span-3 md:col-span-4" data-id="element-1774" data-mp="1uyr99xx1roof">
                  <Textarea label="Aspectos Positivos" value={physicalExamData.positiveAspects} onChange={e => setPhysicalExamData({
                  ...physicalExamData,
                  positiveAspects: e.target.value
                })} rows={2} placeholder="Aspectos positivos del examen físico..." data-id="element-1775" data-mp="1h7cnxkfo3be7" />
                </div>
              </div>
            </FormSection>

            {/* Historia de la Enfermedad Actual */}
            <FormSection title="Historia de la Enfermedad Actual" data-id="element-1776" data-mp="2slq92hj1dcqj">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1777" data-mp="2skf5ahplc4zf">
                <Input label="¿Desde cuándo padece el problema?" value={diseaseHistoryData.sinceWhen} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                sinceWhen: e.target.value
              })} placeholder="Ej: 3 meses, 1 año..." data-id="element-1778" data-mp="2wnbhnqojf4ux" />

                <Input label="¿Se ha tratado con anterioridad?" value={diseaseHistoryData.previousTreatment} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                previousTreatment: e.target.value
              })} placeholder="Sí / No / Descripción..." data-id="element-1779" data-mp="pxrns8qeg59b" />

                <div className="md:col-span-2" data-id="element-1780" data-mp="3ffqpfswx8b02">
                  <Textarea label="Evolución" value={diseaseHistoryData.evolution} onChange={e => setDiseaseHistoryData({
                  ...diseaseHistoryData,
                  evolution: e.target.value
                })} rows={4} placeholder="Descripción de la evolución de la enfermedad actual..." data-id="element-1781" data-mp="3fimnricol1p1" />
                </div>
              </div>
            </FormSection>

            {/* Revisión por Sistemas */}
            <FormSection title="Revisión por Sistemas" data-id="element-1782" data-mp="1xb2daslqotjs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1783" data-mp="22b7zht98pnuh">
                <Input label="General" value={systemsReviewData.general} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                general: e.target.value
              })} data-id="element-1784" data-mp="27386u6rn3j13" />

                <Input label="Cardio-Respiratorio" value={systemsReviewData.cardioRespiratory} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                cardioRespiratory: e.target.value
              })} data-id="element-1785" data-mp="2l3iz9e8ll5od" />

                <Input label="Gastro-Intestinal" value={systemsReviewData.gastrointestinal} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                gastrointestinal: e.target.value
              })} data-id="element-1786" data-mp="3rlk4nzhpgize" />

                <Input label="Genito-Urinario" value={systemsReviewData.genitourinary} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                genitourinary: e.target.value
              })} data-id="element-1787" data-mp="jb8qx0uq9xlb" />

                <Input label="Osteo-Muscular" value={systemsReviewData.osteoMuscular} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                osteoMuscular: e.target.value
              })} data-id="element-1788" data-mp="fetbo8d0c163" />

                <Input label="Neurológico" value={systemsReviewData.neurological} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                neurological: e.target.value
              })} data-id="element-1789" data-mp="y6cdibvap96j" />

                <Input label="Piel & Anexos" value={systemsReviewData.skinAndAppendages} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                skinAndAppendages: e.target.value
              })} data-id="element-1790" data-mp="t56ha42ff3s9" />
              </div>
            </FormSection>

            {/* Diagnóstico */}
            <FormSection title="Diagnóstico" data-id="element-1791" data-mp="3gblskczns352">
              <Textarea value={diagnosis} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.diagnosis) setDiagnosis(e.target.value);
            }} rows={6} placeholder="Escriba el diagnóstico..." maxLength={CHAR_LIMITS.diagnosis} data-id="element-1792" data-mp="3gbdh8pj6p5vz" />

              <p className={`mt-1 text-xs text-right ${diagnosis.length >= CHAR_LIMITS.diagnosis ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1793" data-mp="2xe2s3xr9nsu9">
                {diagnosis.length}/{CHAR_LIMITS.diagnosis} caracteres
              </p>
            </FormSection>

            {/* Tratamiento */}
            <FormSection title="Tratamiento" data-id="element-1794" data-mp="1zxdihhw2rmab">
              <Textarea value={treatment} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.treatment) setTreatment(e.target.value);
            }} rows={10} placeholder="Escriba el tratamiento..." maxLength={CHAR_LIMITS.treatment} data-id="element-1795" data-mp="3i4hp5yxr9b5u" />

              <p className={`mt-1 text-xs text-right ${treatment.length >= CHAR_LIMITS.treatment ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1796" data-mp="3m4i89wvsuaj8">
                {treatment.length}/{CHAR_LIMITS.treatment} caracteres
              </p>
            </FormSection>

            {/* Indicaciones Médicas */}
            <FormSection title="Indicaciones Médicas" data-id="element-1797" data-mp="9oocxwagilul">
              <Textarea value={medicalIndications} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.medicalIndications) setMedicalIndications(e.target.value);
            }} rows={6} placeholder="Indicaciones médicas..." maxLength={CHAR_LIMITS.medicalIndications} data-id="element-1798" data-mp="9n3g200o6qtk" />

              <p className={`mt-1 text-xs text-right ${medicalIndications.length >= CHAR_LIMITS.medicalIndications ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1799" data-mp="1rmkluvsba0lf">
                {medicalIndications.length}/{CHAR_LIMITS.medicalIndications}{' '}
                caracteres
              </p>
            </FormSection>

            {/* Sesiones */}
            <FormSection title="Sesiones" data-id="element-1800" data-mp="1mldg129qbbhe">
              <div className="space-y-4" data-id="element-1801" data-mp="18ogqd45ck023">
                <div className="relative" data-id="element-1802" data-mp="18pjej4baboeb">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-1803" data-mp="1dmic5wu5qdh9" />
                  <input type="text" value={sessionServiceSearch} onChange={e => setSessionServiceSearch(e.target.value)} placeholder="Buscar servicios..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-1804" data-mp="332rgrh1lb29z" />
                </div>
                <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-1805" data-mp="2rnxuicn1m5s9">
                  {filteredSessionServices.map(svc => {
                  const isSelected = selectedSessionServiceIds.includes(svc.id);
                  return <label key={svc.id} className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-1806" data-mp="3am0oz6yd0uxg">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSessionService(svc.id)} className="w-3.5 h-3.5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500" data-id="element-1807" data-mp="ehrf1gfw4pub" />

                        <span className="text-sm text-gray-900 dark:text-white flex-1" data-id="element-1808" data-mp="xf2fv5h67rbv">
                          {svc.name}
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-1809" data-mp="12bj3lykgk3of">
                          {svc.duration} min
                        </span>
                      </label>;
                })}
                </div>
                {selectedSessionServiceIds.length > 0 && <div className="space-y-2" data-id="element-1810" data-mp="1h4cvykktweai">
                    {selectedSessionServiceIds.map(sId => {
                  const svc = services.find(s => s.id === sId);
                  if (!svc) return null;
                  return <div key={sId} className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-1811" data-mp="2duoqnjl5gwas">
                          <div className="flex-1 min-w-0" data-id="element-1812" data-mp="2nlt2iafj411e">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-1813" data-mp="1zv5m10rjs338">
                              {svc.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1814" data-mp="28wddozzk65oc">
                            <span className="text-xs text-gray-500" data-id="element-1815" data-mp="23uxvrgsogfa6">
                              Sesión N°
                            </span>
                            <input type="number" min="1" value={sessionServiceNumbers[sId] || '1'} onChange={e => setSessionServiceNumbers(prev => ({
                        ...prev,
                        [sId]: e.target.value
                      }))} className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500" data-id="element-1816" data-mp="3kbs9w1jy0tie" />

                            <button type="button" onClick={() => toggleSessionService(sId)} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors" data-id="element-1817" data-mp="nyvldbtl41z">
                              <XIcon className="w-4 h-4" data-id="element-1818" data-mp="y2b64394gjln" />
                            </button>
                          </div>
                        </div>;
                })}
                  </div>}
              </div>
            </FormSection>

            {/* Comentarios */}
            <FormSection title="Comentarios" data-id="element-1819" data-mp="xzm1bbenn5ji">
              <Textarea value={comments} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.comments) setComments(e.target.value);
            }} rows={3} placeholder="Comentarios adicionales..." maxLength={CHAR_LIMITS.comments} data-id="element-1820" data-mp="y2gkz0pugmbv" />

              <p className={`mt-1 text-xs text-right ${comments.length >= CHAR_LIMITS.comments ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-1821" data-mp="faff3eenjvsy">
                {comments.length}/{CHAR_LIMITS.comments} caracteres
              </p>
            </FormSection>

            {/* Archivos Adjuntos */}
            <FormSection title="Archivos Adjuntos" data-id="element-1822" data-mp="1c7kve9038lh">
              <div className="space-y-4" data-id="element-1823" data-mp="2cwewmc7n04cp">
                <input ref={imageInputRef} type="file" accept={ATTACH_ACCEPT} multiple onChange={handleImageAttach} className="hidden" data-id="element-1824" data-mp="2czkkxp8fz27d" />

                <button type="button" onClick={() => imageInputRef.current?.click()} className="flex items-center gap-3 w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400" data-id="element-1825" data-mp="2qvy370ksv7mw">
                  <ImageIcon className="w-6 h-6" data-id="element-1826" data-mp="1ajreli7iiqdz" />
                  <div className="text-left" data-id="element-1827" data-mp="1agwuxswbp9lm">
                    <p className="text-sm font-medium" data-id="element-1828" data-mp="1fharoesh4x4c">
                      Adjuntar Imágenes o Documentos
                    </p>
                    <p className="text-xs" data-id="element-1829" data-mp="8va0t1zi2ehd">
                      Imágenes, PDF, Word, Excel y más (máx. 10MB cada uno)
                    </p>
                  </div>
                </button>

                {/* Attachment previews */}
                {attachedImages.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1830" data-mp="dt1spw05a0lk">
                    {attachedImages.map((img, index) => isImageAttachment(img) ? <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1831" data-mp="mtq7isxd44zi">
                          <img src={img.data} alt={img.name} className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setViewingImage(img.data)} data-id="element-1832" data-mp="wmfhfjd9lasq" />

                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2" data-id="element-1833" data-mp="1u5nhvn8625kl">
                            <p className="text-xs text-white truncate" data-id="element-1834" data-mp="1g52xwnqim3pi">
                              {img.name}
                            </p>
                          </div>
                          <button type="button" onClick={() => setAttachedImages(prev => prev.filter((_, i) => i !== index))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600" data-id="element-1835" data-mp="16gkya9tz22ng">
                            <XIcon className="w-3 h-3" data-id="element-1836" data-mp="sficzzaswwdl" />
                          </button>
                        </div> : <div key={index} className="relative group rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center h-48 p-4" data-id="element-1837" data-mp="3blziqe6rdcpv">
                          <a href={img.data} download={img.name} className="flex flex-col items-center justify-center gap-2 text-center w-full" data-id="element-1838" data-mp="3phl66gxcfog6">
                            <FileTextIcon className="w-10 h-10 text-emerald-500" data-id="element-1839" data-mp="75f09gei2la6" />
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate w-full px-1" data-id="element-1840" data-mp="c5b2pb7ju4nm">
                              {img.name}
                            </p>
                            <span className="text-[11px] text-emerald-600 dark:text-emerald-400" data-id="element-1841" data-mp="m2phl6dtqldt">
                              Descargar
                            </span>
                          </a>
                          <button type="button" onClick={() => setAttachedImages(prev => prev.filter((_, i) => i !== index))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600" data-id="element-1842" data-mp="21m6p7c3leuhz">
                            <XIcon className="w-3 h-3" data-id="element-1843" data-mp="2fkwmosubqw6v" />
                          </button>
                        </div>)}
                  </div>}
              </div>
            </FormSection>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 p-4 -mx-4 -mb-4 shadow-lg" data-id="element-1844" data-mp="33biqg7xapub2">
              <Button variant="outline" type="button" onClick={() => {
              setIsEditFormOpen(false);
              setEditingHistory(null);
              resetForm();
            }} data-id="element-1845" data-mp="11g2tshimfodc">
                Cancelar
              </Button>
              <Button type="submit" isLoading={isSaving} data-id="element-1846" data-mp="in8sgqdx7url">
                {isSaving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* View Modal with Print-Optimized Layout */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Historial Clínico" size="full" data-id="element-1847" data-mp="8qm8e3tf57qm">
        {selectedHistory && <div className="print-clinical-history" data-id="element-1848" data-mp="234dnvjyfiltz">
            {/* Print Header - repeats on every printed page via table-header-group */}
            <div className="print-header hidden print:block" data-id="element-1849" data-mp="27zszs6cvm7nr">
              <div className="print-header-inner" data-id="element-1850" data-mp="3skzi1qlygsfh">
                <img src="/images.jpg" alt="Logo" data-id="element-1851" data-mp="3nl4m38nbpcnk" />

                <div data-id="element-1852" data-mp="3nmo5h84a03tk">
                  <h1 data-id="element-1853" data-mp="3nj0didehbpgy">HISTORIA CLÍNICA</h1>
                  <p data-id="element-1854" data-mp="64v75yr3vk8i">Savidna - Centro de Bienestar y Belleza</p>
                </div>
                <div className="print-date" data-id="element-1855" data-mp="1e6fojffen9d">
                  Fecha: {formatDate(selectedHistory.date)}
                </div>
              </div>
            </div>

            {/* Screen Header (visible on screen, hidden on print) */}
            <div className="print:hidden mb-4 space-y-3" data-id="element-1856" data-mp="a5f2qxw16fsj">
              {/* Gradient header card */}
              <div className="rounded-xl overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-500 shadow-sm" data-id="element-1857" data-mp="p267lmoh4jpj">
                <div className="px-4 py-4" data-id="element-1858" data-mp="k55yf2wbv0u7">
                  <div className="flex items-center gap-3" data-id="element-1859" data-mp="2quknyhbddg3u">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1860" data-mp="2qwydybr7024j">
                      <FileTextIcon className="w-6 h-6 text-white" data-id="element-1861" data-mp="1cz2pbdj4bsso" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1862" data-mp="1hxng6mchd04g">
                      <h2 className="text-base font-bold text-white leading-tight" data-id="element-1863" data-mp="1hwcdyqyoo0fg">
                        {selectedHistory.personalBackground.fullName || 'Historia Clínica'}
                      </h2>
                      <p className="text-xs font-medium text-white/80" data-id="element-1864" data-mp="1qw97p6zxjphd">
                        Historia Clínica · {formatDate(selectedHistory.date)}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right" data-id="element-1865" data-mp="z51swqfn2hvh">
                      <p className="text-xs text-white/70" data-id="element-1866" data-mp="137g9t4zqz7k4">Doctor</p>
                      <p className="text-xs font-semibold text-white truncate max-w-[100px]" data-id="element-1867" data-mp="17zzjvrz87kk6">
                        {selectedHistory.doctorSignature || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient info row - compact chips */}
              <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800" data-id="element-1868" data-mp="7ze0733l6eun">
                {[{
              label: 'Edad',
              value: selectedHistory.personalBackground.age ? `${selectedHistory.personalBackground.age} años` : null
            }, {
              label: 'Sexo',
              value: selectedHistory.personalBackground.gender
            }, {
              label: 'Teléfono',
              value: selectedHistory.personalBackground.phone
            }, {
              label: 'Correo',
              value: selectedHistory.personalBackground.email
            }].map(({
              label,
              value
            }) => <div key={label} className="flex items-center justify-between px-4 py-2.5" data-id="element-1869" data-mp="2xj7lly9n86nw">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide" data-id="element-1870" data-mp="2st3som727cng">
                      {label}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1871" data-mp="2itwgi2c29o1x">
                      {value || <span className="text-gray-400 font-normal" data-id="element-1872" data-mp="36jh3cwbc55gh">-</span>}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Print Body - wraps all content sections */}
            <div className="print-body" data-id="element-1873" data-mp="31t0nz6nyf9rh">
              {/* Patient Info Box - print only, screen version is in header above */}
              <div className="print-patient-info mb-6 hidden print:block" data-id="element-1874" data-mp="3picsvuc90vnz">
                <div data-id="element-1875" data-mp="3u9ben9g0alym">
                  <strong data-id="element-1876" data-mp="3u9l32slekl27">Paciente:</strong>{' '}
                  {selectedHistory.personalBackground.fullName}
                </div>
                <div data-id="element-1877" data-mp="1isrjka1pui4p">
                  <strong data-id="element-1878" data-mp="1irzp52juuqg0">Edad:</strong>{' '}
                  {selectedHistory.personalBackground.age} años
                </div>
                <div data-id="element-1879" data-mp="1sknlxxau4ueq">
                  <strong data-id="element-1880" data-mp="1sj44w4hcufbw">Sexo:</strong>{' '}
                  {selectedHistory.personalBackground.gender}
                </div>
                <div data-id="element-1881" data-mp="1nlugms5pc9pn">
                  <strong data-id="element-1882" data-mp="zvgo70j7g8uj">Teléfono:</strong>{' '}
                  {selectedHistory.personalBackground.phone}
                </div>
                <div data-id="element-1883" data-mp="ux6t0pxhd88x">
                  <strong data-id="element-1884" data-mp="uxov436tqgor">Email:</strong>{' '}
                  {selectedHistory.personalBackground.email}
                </div>
                <div data-id="element-1885" data-mp="18u240nxal9rm">
                  <strong data-id="element-1886" data-mp="14ukzpxtlqio1">Fecha:</strong> {formatDate(selectedHistory.date)}
                </div>
              </div>

              {/* Content sections - collapsible on mobile */}
              {(() => {
            const Section = ({
              title,
              color,
              children,
              defaultOpen = false
            }: {
              title: string;
              color: string;
              children: React.ReactNode;
              defaultOpen?: boolean;
            }) => {
              const [open, setOpen] = useState(defaultOpen);
              const hasContent = Children.count(children) > 0;
              return <div className="print-section" data-id="element-1887" data-mp="we2de418w9fk">
                      <h2 className={`font-bold flex items-center justify-between cursor-pointer md:cursor-default select-none print:cursor-default`} onClick={() => setOpen(o => !o)} data-id="element-1888" data-mp="wdaw7y8eae8t">
                        <span className="flex items-center gap-2" data-id="element-1889" data-mp="1bb39skudsskd">
                          <span className={`w-2 h-2 rounded-full ${color} print:hidden`} data-id="element-1890" data-mp="3833elnggvwvw" />

                          {title}
                        </span>
                        <span className="md:hidden print:hidden text-gray-400" data-id="element-1891" data-mp="1u6tpfn9h721b">
                          {open ? <ChevronUpIcon className="w-4 h-4" data-id="element-1892" data-mp="1pg3gy15r6j2e" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-1893" data-mp="1ldysrhy2qen5" />}
                        </span>
                      </h2>
                      <div className={`md:block print:block ${open ? 'block' : 'hidden'}`} data-id="element-1894" data-mp="1gdkj5vgtqwn0">
                        {children}
                      </div>
                    </div>;
            };
            return <div className="space-y-2 print:space-y-6" data-id="element-1895" data-mp="243eug8lhwd55">
                    {/* Antecedentes Personales */}
                    <Section title="Antecedentes Personales" color="bg-blue-500" defaultOpen={true} data-id="element-1896" data-mp="1z89ctlz4lkmv">
                      <div className="print-field-group" data-id="element-1897" data-mp="npoqd1udlovi">
                        <div className="print-field" data-id="element-1898" data-mp="1q2vbvnffgw6x">
                          <div className="print-field-label font-bold" data-id="element-1899" data-mp="1q096nug0esrv">
                            Ocupación:
                          </div>
                          <div className="print-field-value" data-id="element-1900" data-mp="1g3mlf4jrtzpb">
                            {selectedHistory.personalBackground.occupation || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1901" data-mp="1752bosw6v0d8">
                          <div className="print-field-label font-bold" data-id="element-1902" data-mp="12cralmppp8j3">
                            N° de hijos:
                          </div>
                          <div className="print-field-value" data-id="element-1903" data-mp="xe7qzsylc8tw">
                            {selectedHistory.personalBackground.numberOfChildren}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1904" data-mp="jeyhfc4w4cyw">
                          <div className="print-field-label font-bold" data-id="element-1905" data-mp="2v2a4drfet4qa">
                            Tipo de parto:
                          </div>
                          <div className="print-field-value" data-id="element-1906" data-mp="2z56rnvfw9p0k">
                            {selectedHistory.personalBackground.deliveryType || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1907" data-mp="2be1czjlox2ru">
                          <div className="print-field-label font-bold" data-id="element-1908" data-mp="2bfcflg3eg4hd">
                            Alcohol:
                          </div>
                          <div className="print-field-value" data-id="element-1909" data-mp="2lbywxuwioden">
                            {selectedHistory.personalBackground.alcohol || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1910" data-mp="1snjm7kkf4h94">
                          <div className="print-field-label font-bold" data-id="element-1911" data-mp="1smrtchi7gxmj">
                            Tabaco:
                          </div>
                          <div className="print-field-value" data-id="element-1912" data-mp="1pl6pdij8b3qh">
                            {selectedHistory.personalBackground.tobacco || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1913" data-mp="1zbrnz5ywk89e">
                          <div className="print-field-label font-bold" data-id="element-1914" data-mp="1zagnpg4o1in1">
                            Drogas:
                          </div>
                          <div className="print-field-value" data-id="element-1915" data-mp="16m174p5vys9l">
                            {selectedHistory.personalBackground.drugs || '-'}
                          </div>
                        </div>
                        <div className="print-field print-field-full" data-id="element-1916" data-mp="1gl9yda9d9ss9">
                          <div className="print-field-label font-bold" data-id="element-1917" data-mp="1gie17o5cf84v">
                            Infusiones:
                          </div>
                          <div className="print-field-value" data-id="element-1918" data-mp="2ryujdp8a82vf">
                            {selectedHistory.personalBackground.infusions || '-'}
                          </div>
                        </div>
                        <div className="print-field print-field-full" data-id="element-1919" data-mp="8rqhjn3dw2pr">
                          <div className="print-field-label font-bold" data-id="element-1920" data-mp="dt7j7ktozt7e">
                            Motivo de la consulta:
                          </div>
                          <div className="print-field-value" data-id="element-1921" data-mp="4rgd4ev8pkd0">
                            {selectedHistory.personalBackground.consultationReason || '-'}
                          </div>
                        </div>
                      </div>
                    </Section>

                    {/* Antecedentes Clínicos */}
                    <Section title="Antecedentes Clínicos" color="bg-red-500" data-id="element-1922" data-mp="iqfxhabpzd3d">
                      <div className="space-y-0" data-id="element-1923" data-mp="npiy8iko2x5j">
                        <div className="print-field" data-id="element-1924" data-mp="nnze2gvw41yh">
                          <div className="print-field-label font-bold" data-id="element-1925" data-mp="1bevbpun11rdn">
                            Enfermedades:
                          </div>
                          <div className="print-field-value" data-id="element-1926" data-mp="1g8g4975upt5g">
                            {selectedHistory.clinicalBackground.diseases || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1927" data-mp="3itg2497wc3ws">
                          <div className="print-field-label font-bold" data-id="element-1928" data-mp="3eqb5uoeesygl">
                            Medicamentos actuales:
                          </div>
                          <div className="print-field-value" data-id="element-1929" data-mp="ab1w84uge4yv">
                            {selectedHistory.clinicalBackground.currentMedications || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1930" data-mp="1hoss8yrjkyj">
                          <div className="print-field-label font-bold" data-id="element-1931" data-mp="3spj51hako6ks">
                            Enfermedades crónicas:
                          </div>
                          <div className="print-field-value" data-id="element-1932" data-mp="u0u6mb17qw2s">
                            {selectedHistory.clinicalBackground.chronicDiseases || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1933" data-mp="11ijvu2kxzzks">
                          <div className="print-field-label font-bold" data-id="element-1934" data-mp="11jbq9a2szr9h">
                            Antecedentes quirúrgicos:
                          </div>
                          <div className="print-field-value" data-id="element-1935" data-mp="1ajzxnlxoytde">
                            {selectedHistory.clinicalBackground.surgicalHistory || '-'}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1936" data-mp="1kfth7p9tvn4o">
                          <div className="print-field-label font-bold" data-id="element-1937" data-mp="1p7urj5nfr3s2">
                            Implantes/Dispositivos:
                          </div>
                          <div className="print-field-value" data-id="element-1938" data-mp="1u9k1ehyx9bek">
                            {selectedHistory.clinicalBackground.implantsOrDevices || '-'}
                            {selectedHistory.clinicalBackground.implantType && ` - Tipo: ${selectedHistory.clinicalBackground.implantType}`}
                          </div>
                        </div>
                        <div className="print-field" data-id="element-1939" data-mp="3v7vtewk08g9a">
                          <div className="print-field-label font-bold" data-id="element-1940" data-mp="cwhd8q82ugln">
                            Alergias:
                          </div>
                          <div className="print-field-value" data-id="element-1941" data-mp="8367ksf045vb">
                            {selectedHistory.clinicalBackground.allergies || '-'}
                          </div>
                        </div>
                      </div>
                    </Section>

                    {/* Exploración Física */}
                    {selectedHistory.physicalExam && (selectedHistory.physicalExam.bloodPressure || selectedHistory.physicalExam.heartRate || selectedHistory.physicalExam.bloodSugar || selectedHistory.physicalExam.temperature || selectedHistory.physicalExam.weight || selectedHistory.physicalExam.height || selectedHistory.physicalExam.lungs || selectedHistory.physicalExam.eyes || selectedHistory.physicalExam.throat || selectedHistory.physicalExam.abdomen || (selectedHistory.physicalExam as any).positiveAspects || (selectedHistory.physicalExam as any).respiratoryRate || (selectedHistory.physicalExam as any).weightLb) && <Section title="Exploración Física" color="bg-purple-500" data-id="element-1942" data-mp="gfpyg73ompqg">
                          <div className="print-field-group" data-id="element-1943" data-mp="2aspjifqu0c54">
                            {selectedHistory.physicalExam.bloodPressure && <div className="print-field" data-id="element-1944" data-mp="2er5d52hf4iri">
                                <div className="print-field-label font-bold" data-id="element-1945" data-mp="12iuuprvguooc">
                                  T.A. (Presión Arterial):
                                </div>
                                <div className="print-field-value" data-id="element-1946" data-mp="stl127kz4xy4">
                                  {selectedHistory.physicalExam.bloodPressure}{' '}
                                  mmHg
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.heartRate && <div className="print-field" data-id="element-1947" data-mp="ewoeiibw22n1">
                                <div className="print-field-label font-bold" data-id="element-1948" data-mp="9w0dr4xuy7fm">
                                  F.C. (Frec. Cardíaca):
                                </div>
                                <div className="print-field-value" data-id="element-1949" data-mp="249rrogn7zdgv">
                                  {selectedHistory.physicalExam.heartRate} Vm
                                </div>
                              </div>}
                            {(selectedHistory.physicalExam as any).respiratoryRate && <div className="print-field" data-id="element-1950" data-mp="2bqeofvtza6gu">
                                <div className="print-field-label font-bold" data-id="element-1951" data-mp="2gk10pmw8c8c4">
                                  F.R. (Frec. Respiratoria):
                                </div>
                                <div className="print-field-value" data-id="element-1952" data-mp="1ot36gee7sfoj">
                                  {(selectedHistory.physicalExam as any).respiratoryRate}{' '}
                                  r/m
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.bloodSugar && <div className="print-field" data-id="element-1953" data-mp="3e6q686q5532q">
                                <div className="print-field-label font-bold" data-id="element-1954" data-mp="3e92pcn7mxjhd">
                                  Azúcar en Sangre:
                                </div>
                                <div className="print-field-value" data-id="element-1955" data-mp="1niuggxaz2sdn">
                                  {selectedHistory.physicalExam.bloodSugar}{' '}
                                  mg/dL
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.weight && <div className="print-field" data-id="element-1956" data-mp="14te3vbm0j9gv">
                                <div className="print-field-label font-bold" data-id="element-1957" data-mp="14qrwbbz4gtyn">
                                  Peso (Kg):
                                </div>
                                <div className="print-field-value" data-id="element-1958" data-mp="vt7vu59331p3">
                                  {selectedHistory.physicalExam.weight} kg
                                </div>
                              </div>}
                            {(selectedHistory.physicalExam as any).weightLb && <div className="print-field" data-id="element-1959" data-mp="30ae1b45wf5uh">
                                <div className="print-field-label font-bold" data-id="element-1960" data-mp="2vbugx86ye248">
                                  Peso (Lb):
                                </div>
                                <div className="print-field-value" data-id="element-1961" data-mp="34br93jsjxj41">
                                  {(selectedHistory.physicalExam as any).weightLb}{' '}
                                  lb
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.height && <div className="print-field" data-id="element-1962" data-mp="3nxm6lm3buewi">
                                <div className="print-field-label font-bold" data-id="element-1963" data-mp="1xoc7rw9dqxzp">
                                  Altura:
                                </div>
                                <div className="print-field-value" data-id="element-1964" data-mp="3sqgzppfrdxqb">
                                  {selectedHistory.physicalExam.height} cm
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.temperature && <div className="print-field" data-id="element-1965" data-mp="bivdteb9g3bj">
                                <div className="print-field-label font-bold" data-id="element-1966" data-mp="bg969eoddntb">
                                  Temperatura:
                                </div>
                                <div className="print-field-value" data-id="element-1967" data-mp="khph1nj3jry2">
                                  {selectedHistory.physicalExam.temperature} °C
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.lungs && <div className="print-field" data-id="element-1968" data-mp="z9rd0wi1q0st">
                                <div className="print-field-label font-bold" data-id="element-1969" data-mp="138qf89ce7jgk">
                                  Pulmones:
                                </div>
                                <div className="print-field-value" data-id="element-1970" data-mp="2np7pefzsyd73">
                                  {selectedHistory.physicalExam.lungs}
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.eyes && <div className="print-field" data-id="element-1971" data-mp="37fjoclqpwqy0">
                                <div className="print-field-label font-bold" data-id="element-1972" data-mp="37f04gzhibshc">
                                  Ojos:
                                </div>
                                <div className="print-field-value" data-id="element-1973" data-mp="2xp6ug2x0jlf7">
                                  {selectedHistory.physicalExam.eyes}
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.throat && <div className="print-field" data-id="element-1974" data-mp="3lf1b6vkgdu4q">
                                <div className="print-field-label font-bold" data-id="element-1975" data-mp="35xb24gcmcz6x">
                                  Garganta:
                                </div>
                                <div className="print-field-value" data-id="element-1976" data-mp="3a0g48hsseyv1">
                                  {selectedHistory.physicalExam.throat}
                                </div>
                              </div>}
                            {selectedHistory.physicalExam.abdomen && <div className="print-field" data-id="element-1977" data-mp="3ookldhc6z2bp">
                                <div className="print-field-label font-bold" data-id="element-1978" data-mp="3tlua0urrb9yh">
                                  Abdomen:
                                </div>
                                <div className="print-field-value" data-id="element-1979" data-mp="3jx2n511jzfuf">
                                  {selectedHistory.physicalExam.abdomen}
                                </div>
                              </div>}
                            {(selectedHistory.physicalExam as any).positiveAspects && <div className="print-field print-field-full" data-id="element-1980" data-mp="20wtnsiu69eei">
                                <div className="print-field-label font-bold" data-id="element-1981" data-mp="1vy8msh73iqp0">
                                  Aspectos Positivos:
                                </div>
                                <div className="print-field-value" data-id="element-1982" data-mp="25pd0zeoy67is">
                                  {(selectedHistory.physicalExam as any).positiveAspects}
                                </div>
                              </div>}
                          </div>
                        </Section>}

                    {/* Historia de la Enfermedad Actual */}
                    {(selectedHistory as any).diseaseHistory && ((selectedHistory as any).diseaseHistory.sinceWhen || (selectedHistory as any).diseaseHistory.previousTreatment || (selectedHistory as any).diseaseHistory.evolution) && <Section title="Historia de la Enfermedad Actual" color="bg-orange-500" data-id="element-1983" data-mp="2004lyufi3tb4">
                          <div className="print-field-group" data-id="element-1984" data-mp="291kyb7pvm5hz">
                            {(selectedHistory as any).diseaseHistory.sinceWhen && <div className="print-field" data-id="element-1985" data-mp="1gdgqafju1gc4">
                                <div className="print-field-label font-bold" data-id="element-1986" data-mp="1gbvrgdqhjb8g">
                                  ¿Desde cuándo padece el problema?
                                </div>
                                <div className="print-field-value" data-id="element-1987" data-mp="1q52ys44sd1b6">
                                  {(selectedHistory as any).diseaseHistory.sinceWhen}
                                </div>
                              </div>}
                            {(selectedHistory as any).diseaseHistory.previousTreatment && <div className="print-field" data-id="element-1988" data-mp="g478idhm9mfg">
                                <div className="print-field-label font-bold" data-id="element-1989" data-mp="c4q4zplr2zcx">
                                  ¿Se ha tratado con anterioridad?
                                </div>
                                <div className="print-field-value" data-id="element-1990" data-mp="2enbloapvje2">
                                  {(selectedHistory as any).diseaseHistory.previousTreatment}
                                </div>
                              </div>}
                            {(selectedHistory as any).diseaseHistory.evolution && <div className="print-field print-field-full" data-id="element-1991" data-mp="17c7eksrrpvgo">
                                <div className="print-field-label font-bold" data-id="element-1992" data-mp="17a4flj8n0rzt">
                                  Evolución:
                                </div>
                                <div className="print-field-value whitespace-pre-wrap" data-id="element-1993" data-mp="yahgj43dry9x">
                                  {(selectedHistory as any).diseaseHistory.evolution}
                                </div>
                              </div>}
                          </div>
                        </Section>}

                    {/* Revisión por Sistemas */}
                    {(selectedHistory as any).systemsReview && Object.values((selectedHistory as any).systemsReview).some(Boolean) && <Section title="Revisión por Sistemas" color="bg-cyan-500" data-id="element-1994" data-mp="ao14vrfzocvj">
                          <div className="print-field-group" data-id="element-1995" data-mp="3i7hhidbv3s23">
                            {(selectedHistory as any).systemsReview.general && <div className="print-field" data-id="element-1996" data-mp="3w3w2qrjfhiye">
                                <div className="print-field-label font-bold" data-id="element-1997" data-mp="3s3ucdej58byf">
                                  General:
                                </div>
                                <div className="print-field-value" data-id="element-1998" data-mp="507cr50ipfu1">
                                  {(selectedHistory as any).systemsReview.general}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.cardioRespiratory && <div className="print-field" data-id="element-1999" data-mp="1095evot8p6qq">
                                <div className="print-field-label font-bold" data-id="element-2000" data-mp="107kkq0au7pte">
                                  Cardio-Respiratorio:
                                </div>
                                <div className="print-field-value" data-id="element-2001" data-mp="1a7l7xxbtuq2v">
                                  {(selectedHistory as any).systemsReview.cardioRespiratory}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.gastrointestinal && <div className="print-field" data-id="element-2002" data-mp="mhh1li798c86">
                                <div className="print-field-label font-bold" data-id="element-2003" data-mp="mfxlbrllm16e">
                                  Gastro-Intestinal:
                                </div>
                                <div className="print-field-value" data-id="element-2004" data-mp="3pxxwtgqd4oor">
                                  {(selectedHistory as any).systemsReview.gastrointestinal}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.genitourinary && <div className="print-field" data-id="element-2005" data-mp="2o7fxot1yl9in">
                                <div className="print-field-label font-bold" data-id="element-2006" data-mp="2t6j4ahxl7nsq">
                                  Genito-Urinario:
                                </div>
                                <div className="print-field-value" data-id="element-2007" data-mp="2f8kqper3bvea">
                                  {(selectedHistory as any).systemsReview.genitourinary}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.osteoMuscular && <div className="print-field" data-id="element-2008" data-mp="35ibq34t1obl0">
                                <div className="print-field-label font-bold" data-id="element-2009" data-mp="30ogzdhf0uwcf">
                                  Osteo-Muscular:
                                </div>
                                <div className="print-field-value" data-id="element-2010" data-mp="2rokg5vbeipor">
                                  {(selectedHistory as any).systemsReview.osteoMuscular}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.neurological && <div className="print-field" data-id="element-2011" data-mp="bxpvfge48evh">
                                <div className="print-field-label font-bold" data-id="element-2012" data-mp="by7oy59e8ezp">
                                  Neurológico:
                                </div>
                                <div className="print-field-value" data-id="element-2013" data-mp="1ytvzwrllww8g">
                                  {(selectedHistory as any).systemsReview.neurological}
                                </div>
                              </div>}
                            {(selectedHistory as any).systemsReview.skinAndAppendages && <div className="print-field" data-id="element-2014" data-mp="1jwn85wse5qg7">
                                <div className="print-field-label font-bold" data-id="element-2015" data-mp="1jz9e5rzmvxwb">
                                  Piel & Anexos:
                                </div>
                                <div className="print-field-value" data-id="element-2016" data-mp="2vbaabrytuom3">
                                  {(selectedHistory as any).systemsReview.skinAndAppendages}
                                </div>
                              </div>}
                          </div>
                        </Section>}

                    {/* Diagnóstico */}
                    <Section title="Diagnóstico" color="bg-emerald-500" data-id="element-2017" data-mp="33sj7qftj4rgb">
                      <div className="print-field-value whitespace-pre-wrap" data-id="element-2018" data-mp="3hsk69ombnmsm">
                        {selectedHistory.diagnosis || '-'}
                      </div>
                    </Section>

                    {/* Tratamiento */}
                    <Section title="Tratamiento" color="bg-teal-500" data-id="element-2019" data-mp="3ridj0sy7a7yg">
                      <div className="print-field-value whitespace-pre-wrap" data-id="element-2020" data-mp="3mjk36tvfy4v4">
                        {selectedHistory.treatment || '-'}
                      </div>
                    </Section>

                    {/* Indicaciones Médicas */}
                    <Section title="Indicaciones Médicas" color="bg-teal-500" data-id="element-2021" data-mp="3vmbg6y3yt8ov">
                      <div className="print-field-value whitespace-pre-wrap" data-id="element-2022" data-mp="j27dptvlnipo">
                        {selectedHistory.medicalIndications || '-'}
                      </div>
                    </Section>

                    {/* Sesiones */}
                    <Section title="Sesiones" color="bg-indigo-500" data-id="element-2023" data-mp="e1t6uf5qiet8">
                      {selectedHistory.sessionServices && selectedHistory.sessionServices.length > 0 ? <div className="space-y-2 mt-2" data-id="element-2024" data-mp="514s1i8ioafa">
                          {selectedHistory.sessionServices.map((ss, idx) => <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50" data-id="element-2025" data-mp="3minzz2wqcku4" data-mpi={idx}>
                              <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2026" data-mp="3ifrmtrpylgxk">
                                {ss.serviceName}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2027" data-mp="1j92tf34px05a">
                                Sesión N°{' '}
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-2028" data-mp="1e97zsrtk5wgj">
                                  {ss.sessionNumber}
                                </span>
                              </span>
                            </div>)}
                        </div> : <div className="print-field-group" data-id="element-2029" data-mp="21xratf3zsncb">
                          <div className="print-field" data-id="element-2030" data-mp="1xwnukri18pak">
                            <div className="print-field-label font-bold" data-id="element-2031" data-mp="1sz5w5v1n5sgt">
                              Sesiones:
                            </div>
                            <div className="print-field-value" data-id="element-2032" data-mp="mjlia8myulw7">
                              {selectedHistory.sessions || '-'}
                            </div>
                          </div>
                          <div className="print-field" data-id="element-2033" data-mp="2t8dmewe6uhzg">
                            <div className="print-field-label font-bold" data-id="element-2034" data-mp="2y5xdiwvzip8t">
                              N° de sesión:
                            </div>
                            <div className="print-field-value" data-id="element-2035" data-mp="3267ji9js1fno">
                              {selectedHistory.sessionNumber || '-'}
                            </div>
                          </div>
                        </div>}
                    </Section>

                    {/* Comentarios */}
                    <Section title="Comentarios" color="bg-gray-400" data-id="element-2036" data-mp="3gyrgesgc2qwo">
                      <div className="print-field-value whitespace-pre-wrap" data-id="element-2037" data-mp="3lx2r15egd5fy">
                        {selectedHistory.comments || '-'}
                      </div>
                    </Section>

                    {/* Firma */}
                    <div className="print-signature" data-id="element-2038" data-mp="1rjth5chzvhx6">
                      <div className="print-signature-line" data-id="element-2039" data-mp="1vkdby9f3i9g5">
                        <p className="font-bold text-gray-900 dark:text-white" data-id="element-2040" data-mp="1cn3cj6f4z6so">
                          {selectedHistory.doctorSignature}
                        </p>
                      </div>
                      <p data-id="element-2041" data-mp="1lfdpgvhf1qdm">Firma del Dr. (a)</p>
                    </div>

                    {/* Imágenes Adjuntas */}
                    {selectedHistory.attachedImages && selectedHistory.attachedImages.length > 0 && <Section title="Archivos Adjuntos" color="bg-pink-500" data-id="element-2042" data-mp="tp900iesud5j">
                          <div className="grid grid-cols-1 gap-3 mt-2" data-id="element-2043" data-mp="opwb7s4daw0b">
                            {selectedHistory.attachedImages.map((img, index) => isImageAttachment(img) ? <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-2044" data-mp="xorg90voe021">
                                  <img src={img.data} alt={img.name || `Imagen ${index + 1}`} className="w-full h-auto max-h-64 object-contain bg-gray-50 dark:bg-gray-900 cursor-pointer" onClick={() => setViewingImage(img.data)} data-id="element-2045" data-mp="2hlzzfn6kforg" />

                                  <div className="p-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700" data-id="element-2046" data-mp="30cq49hfp5lmy">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1.5" data-id="element-2047" data-mp="35aj9o896ebm6">
                                      <ImageIcon className="w-3 h-3" data-id="element-2048" data-mp="2qmeomxmnho08" />
                                      {img.name || `Imagen ${index + 1}`}
                                    </p>
                                  </div>
                                </div> : <a key={index} href={img.data} download={img.name || `archivo-${index + 1}`} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-50 dark:bg-gray-800 hover:border-emerald-400 dark:hover:border-emerald-500 transition-colors" data-id="element-2049" data-mp="3o3dcy4xfsfak">
                                  <FileTextIcon className="w-6 h-6 text-emerald-500 flex-shrink-0" data-id="element-2050" data-mp="azdwzp8ww9gj" />
                                  <span className="text-sm text-gray-700 dark:text-gray-200 truncate flex-1" data-id="element-2051" data-mp="fxfq7301xp5q">
                                    {img.name || `Documento ${index + 1}`}
                                  </span>
                                  <span className="text-xs text-emerald-600 dark:text-emerald-400 flex-shrink-0" data-id="element-2052" data-mp="ov9atgogeud2">
                                    Descargar
                                  </span>
                                </a>)}
                          </div>
                        </Section>}
                  </div>;
          })()}

              {/* Actions (hidden on print) */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 no-print print:hidden" data-id="element-2053" data-mp="3c0djvu7w2u0v">
                <Button variant="outline" leftIcon={<FileDownIcon className="w-4 h-4" data-id="element-2055" data-mp="3227iddpzvpwj" />} onClick={() => selectedHistory && handleDownloadPdf(selectedHistory)} isLoading={isGeneratingPdf} data-id="element-2054" data-mp="32425myoqnu3s">
                  {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
                </Button>
                <Button onClick={() => setIsViewOpen(false)} data-id="element-2056" data-mp="rcsioquvzlln">Cerrar</Button>
              </div>
            </div>
          </div>}
      </Modal>

      {/* Image Viewer Modal */}
      {viewingImage && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setViewingImage(null)} data-id="element-2057" data-mp="15hj1sp4rxf0m">
          <div className="relative max-w-4xl max-h-[90vh] w-full" data-id="element-2058" data-mp="whcujkzo5b9w">
            <button onClick={() => setViewingImage(null)} className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors" data-id="element-2059" data-mp="wf04slno5wma">
              <XIcon className="w-6 h-6 text-white" data-id="element-2060" data-mp="1p6jumoeru8x9" />
            </button>
            <img src={viewingImage} alt="Vista ampliada" className="w-full h-full object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2061" data-mp="1t62d7dj4e7wx" />
          </div>
        </div>}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Historiales Clínicos" isExporting={isExporting} data-id="element-2062" data-mp="3rnrl23ef15n" />

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Historiales Clínicos a PDF" isExporting={isPdfExporting} data-id="element-2063" data-mp="19u3n20zfazi" />
    </div>;
}